// The operator app. A SEPARATE bundle from the customer SPA: admin code has
// no business shipping to every paying customer, and an operator screen has
// different needs from a trading screen -- dense, factual, no persuasion.
//
// Phone-first like the rest of the product: an operator checks the system from
// wherever they are, and "is it alive" must answer on a 390px screen.
import React, { useCallback, useEffect, useRef, useState } from "https://esm.sh/react@18.2.0";
import { createRoot } from "https://esm.sh/react-dom@18.2.0/client";
import htm from "https://esm.sh/htm@3.1.1";

const html = htm.bind(React.createElement);

// ---------------------------------------------------------------- utilities

function token() {
  try {
    return sessionStorage.getItem("access_token") || localStorage.getItem("access_token") || "";
  } catch (e) {
    return "";
  }
}

// Every field in this app carries a hint, by the owner's instruction ("bade
// hint had kil shi ma3 description"). The rule applied: a hint says what the
// field DOES and what goes wrong if it is wrong -- not what it is called.
// "Bot token" as a hint under a box labelled "Bot token" is decoration;
// "BotFather gave you this; anyone holding it can post as your bot" is a hint.
//
// It lives here, above api(), because every screen below uses it. It started
// beside the two screens that introduced it and NewCustomer -- the form the
// whole business runs through -- could not reach it.
function Field({ label, hint, children }) {
  return html`
    <label className="op-field">
      <span className="op-field-label">${label}</span>
      ${children}
      <small className="op-hint">${hint}</small>
    </label>`;
}

async function api(path, options = {}) {
  // The session usually lives in the HTTP-only cookie, not in storage: the
  // operator port is its own origin, so a sign-in on the customer app leaves
  // NOTHING in this origin's localStorage. Sending "Bearer <empty>" anyway
  // made the server judge the empty header instead of the valid cookie, and
  // every screen 401'd for an operator who was in fact signed in -- which
  // then looped /admin -> /login -> /admin on every refresh.
  const bearer = token();
  const response = await fetch(path, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
    },
  });
  // An operator screen that silently shows nothing is worse than one that
  // says "sign in": the difference between "system is fine" and "I cannot see
  // the system" must never be ambiguous.
  //
  // A 401 gets ONE silent refresh before it becomes a sign-out: operator
  // access tokens age out hourly, and bouncing the owner to the customer
  // login mid-shift -- losing the screen they were on -- was the single
  // weakest thing about this app. The refresh cookie is HTTP-only; the
  // server decides if it is still good.
  if (response.status === 401 && !options._retried) {
    try {
      const stored = sessionStorage.getItem('refresh_token');
      if (stored) {
        const refreshed = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored }),
        });
        if (refreshed.ok) {
          const payload = await refreshed.json();
          if (payload && payload.access_token) {
            try { sessionStorage.setItem('access_token', payload.access_token); }
            catch (e) { /* private mode */ }
          }
          return api(path, { ...options, _retried: true });
        }
      }
    } catch (e) { /* fall through to the honest sign-out */ }
  }
  // The two refusals are different destinations and must not be merged. 401
  // means no session, and the answer is the login page. 403 means a session
  // that is not an operator's, and sending THAT to the login page starts a
  // loop: the login page sees a valid token, sends them back to /admin, which
  // refuses them again.
  if (response.status === 401) throw new Error("signed-out");
  if (response.status === 403) throw new Error("not-operator");
  // The operator app answers on its OWN port. Opened on the customer port,
  // every route here 404s with {error: "not_here"} and the screen said
  // "Could not reach the server" -- which is both wrong and unactionable: the
  // server is fine, the address is not. The owner hit exactly this and
  // reported it as "I cannot create a user", because the create form is the
  // screen they were on when the API refused them.
  if (response.status === 404) {
    let payload = null;
    try { payload = await response.clone().json(); } catch (e) { payload = null; }
    if (payload && payload.error === "not_here") throw new Error("wrong-port");
  }
  return response.json();
}

async function signOutThenSignIn() {
  // Drop the session that is not an operator's BEFORE going to the login page.
  // Otherwise the login page finds a valid token, decides we are already
  // signed in, and returns us to the screen that just refused us.
  const back = encodeURIComponent(window.location.pathname);
  try {
    const bearer = token();
    await fetch("/api/auth/logout", {
      method: "POST",
      // No header when storage is empty: the cookie IS the session then, and
      // an empty Bearer header would make the server refuse the logout.
      headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
    });
  } catch (e) { /* offline: the stored copies below still go */ }
  try {
    sessionStorage.removeItem("access_token");
    localStorage.removeItem("access_token");
  } catch (e) { /* storage can be denied; the cookie is the one that counts */ }
  window.location.replace(`/login?next=${back}`);
}

// Which product a call belongs to. Each style publishes on its own home
// frame, so the frame is the style -- and the operator's first question about
// any row is which profile produced it.
function styleOf(timeframe) {
  // The historical record names what PRODUCED a call, retired styles
  // included: a ledger that cannot say what a swing call was is a ledger
  // lying by omission, and swing was withdrawn on 2026-08-14 with three
  // years of published rows behind it.
  //
  // Retired frames are marked, so nobody reads an old row as an offer. The
  // shipping half of this map is scalping and intraday; if a style is ever
  // added, this is the second place after src/trading/styles.py that has to
  // know, which is exactly the duplication that let a retired style reappear
  // on this screen for an afternoon.
  const shipping = { "5m": "scalping", "1h": "intraday" };
  const retired = { "4h": "swing" };
  if (shipping[timeframe]) return shipping[timeframe];
  if (retired[timeframe]) return `${retired[timeframe]} (retired)`;
  return timeframe || "—";
}

// The outcome in words, derived from the PRICES -- never from the raw status.
// "expired" is not something that happens to a trade. A call whose entry never
// traded was never opened; a call closed by its validity window touched
// neither level. Both were rendering as one flat word that said neither.
function outcomeOf(s) {
  if (s.status === "open") return "live";
  if (s.status === "expired") return "never opened";
  const exit = s.exit_price;
  if (exit == null) return s.status === "won" ? "closed up" : "closed down";
  const buy = String(s.side || "").toLowerCase() === "buy";
  const reached = (level) => level != null &&
    (buy ? exit >= Number(level) - 0.01 : exit <= Number(level) + 0.01);
  const hitStop = (level) => level != null &&
    (buy ? exit <= Number(level) + 0.01 : exit >= Number(level) - 0.01);
  if (reached(s.target)) return "target hit";
  if (hitStop(s.stop)) return "stop hit";
  return s.status === "won" ? "closed up, short of target"
                            : "closed down, short of stop";
}

function outcomeClass(s) {
  if (s.status === "open") return "";
  if (s.status === "expired") return "muted";
  return (s.r_multiple || 0) > 0 ? "pos" : (s.r_multiple || 0) < 0 ? "neg" : "muted";
}

// Did it win, did it lose, or has the market not answered yet?
//
// Three states, and the third one is real: a call that is still live and a
// call whose entry never traded have both given the operator NOTHING to
// score. That is not a fourth outcome and it is certainly not "expired" --
// it is an answer that has not arrived. Anything else would count a call
// that never opened as a loss, which would be a lie in the only column the
// owner reads first.
//
// The verdict comes from R, which is computed from the prices. Status is a
// fallback for a resolved row that was never scored, never the source.
//
// The verdict is read at the SAME precision the R column prints. A call that
// made 0.002R is a win by arithmetic and a scratch by every other measure, and
// it used to render the word "won" in green beside the number "0.00" -- the
// operator's eye lands on a contradiction and stops trusting the column. The
// word and the number now always agree, because they are the same value.
const scored = (r) => Number(Number(r).toFixed(2));
const FLAT_WHY = "closed level -- inside a hundredth of R either way";

function resultOf(s) {
  const pending = (why) => ({ key: "pending", word: "no result yet", cls: "res-pending", why });
  if (s.status === "open") return pending("still live -- the market has not decided");
  if (s.status === "expired") return pending("the entry price never traded, so nothing opened");
  const r = s.r_multiple;
  if (r == null) {
    if (s.status === "won") return { key: "won", word: "won", cls: "res-won", why: "closed in profit" };
    if (s.status === "lost") return { key: "lost", word: "lost", cls: "res-lost", why: "closed at a loss" };
    return pending("resolved but never scored");
  }
  const shown = scored(r);
  if (shown > 0) return { key: "won", word: "won", cls: "res-won", why: outcomeOf(s) };
  if (shown < 0) return { key: "lost", word: "lost", cls: "res-lost", why: outcomeOf(s) };
  return { key: "flat", word: "break-even", cls: "res-flat", why: FLAT_WHY };
}

// The same question for a trade, which carries no status word at all: an
// open trade has no exit, and that is the whole of it.
function tradeResultOf(t) {
  if (t.exit_price == null) return { key: "pending", word: "no result yet", cls: "res-pending", why: "still open" };
  const r = t.r_multiple;
  if (r == null) return { key: "pending", word: "no result yet", cls: "res-pending", why: "closed but never scored" };
  const shown = scored(r);
  if (shown > 0) return { key: "won", word: "won", cls: "res-won", why: t.exit_reason || "closed in profit" };
  if (shown < 0) return { key: "lost", word: "lost", cls: "res-lost", why: t.exit_reason || "closed at a loss" };
  return { key: "flat", word: "break-even", cls: "res-flat", why: FLAT_WHY };
}

// How long the market took to decide it.
function heldFor(s) {
  if (!s.published_at || !s.resolved_at) return "—";
  const mins = Math.max(0, Math.round((s.resolved_at - s.published_at) / 60));
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
}

function ago(iso) {
  if (!iso) return "—";
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

function money(v) {
  return v == null ? "—" : Number(v).toFixed(2);
}

// A number is never shown without its age when age is what makes it true.
function Light({ ok, warn, children }) {
  const cls = ok ? "ok" : (warn ? "warn" : "bad");
  return html`<span className=${`light light-${cls}`}>${children}</span>`;
}

// A block the server could not read.
//
// health.py used to write null for a block whose try/except fired, and every
// screen read null as a READING: `risk: null` rendered "HALTED" in red on
// Control and "publishing allowed" in green on Actions -- one payload, two
// tabs, opposite claims. `record: null` rendered "chain intact · 0 entries",
// which is the tamper evidence this product is SOLD on reporting verified
// when it was never read. It now arrives as {unavailable: true, error}, and
// the only thing this console will say about it is that it was not reported.
function missing(block) {
  return !block || block.unavailable === true;
}

function NotReported({ block, children }) {
  return html`
    <span className="not-reported"
          title=${(block && block.error) || "the server could not read this"}>
      ${children || "not reported"}
    </span>`;
}

// Every measurement the control room claims to make. A block that failed is a
// PROBLEM in triage, never an absence of problems: "Everything is healthy"
// over three unread measurements is the worst sentence this screen can print.
const MEASUREMENTS = [
  ["risk", "the publishing halts"],
  ["record", "the published record"],
  ["absence", "the absence test"],
  ["business", "the business numbers"],
  ["alerts", "alert delivery"],
  ["db", "the database"],
  ["feeds", "the external feeds"],
  ["archive", "the archive"],
  ["flow_today", "today's flow"],
];

// ------------------------------------------------------------------ screens

// Forcing one feed to re-fetch. The control room could show a feed was down
// and the operator could do nothing about it but restart the process -- and a
// recovered source would stay dark for the length of the failure backoff.
function FeedRefresh({ feed }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setBusy(true);
    setState(null);
    try {
      const r = await api("/api/admin/refresh-feed", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feed }),
      });
      // A refused refresh says so. A button that reports success whatever
      // happened is worse than no button.
      setState(r.success ? "ok" : (r.detail || r.error || "failed"));
    } catch (e) { setState("failed"); }
    setBusy(false);
  }, [feed]);

  return html`
    <span className="feed-refresh">
      <button disabled=${busy} onClick=${refresh}>${busy ? "…" : "refresh"}</button>
      ${state && html`
        <small className=${state === "ok" ? "pos" : "neg"}>
          ${state === "ok" ? "fetched" : String(state).slice(0, 40)}
        </small>`}
    </span>`;
}

// The single answer an operator opens this app for. Six cards is a diagnosis;
// this is the triage, and it names the WORST thing rather than averaging.
function overallState(health) {
  const problems = [];
  // A measurement that did not happen is the first thing to say, not a fact
  // to render. Each unread block is its own line, named, with the server's
  // reason in the tooltip -- because "risk: not reported" and "publishing
  // halted" are different emergencies and only one of them is about trading.
  for (const [key, label] of MEASUREMENTS) {
    if (missing(health[key])) {
      problems.push({ level: "bad", text: `${label}: not reported`,
                      why: (health[key] || {}).error });
    }
  }
  if (!missing(health.risk) && !health.risk.publishing_allowed) {
    problems.push({ level: "halt", text: health.risk.blocked_by || "publishing halted" });
  }
  const age = health.price?.tick_store?.age_s;
  if (age == null) problems.push({ level: "bad", text: "no price age recorded" });
  else if (age > 300) problems.push({ level: "bad", text: `price ${Math.round(age)}s old` });
  else if (age > 60) problems.push({ level: "warn", text: `price ${Math.round(age)}s old` });
  if (health.record?.chain?.intact === false) {
    problems.push({ level: "bad", text: "published record chain BROKEN" });
  }
  if (health.db?.integrity && health.db.integrity !== "ok") {
    problems.push({ level: "bad", text: `database ${health.db.integrity}` });
  }
  if (!health.loop?.running) problems.push({ level: "bad", text: "analysis loop stopped" });
  if (!missing(health.alerts) && (health.alerts.subscribers || 0) === 0) {
    problems.push({ level: "warn", text: "no alert subscribers" });
  }
  if (!missing(health.feeds)) {
    for (const feed of Object.values(health.feeds)) {
      if (feed.backing_off) problems.push({ level: "warn", text: `${feed.name} is down` });
    }
  }
  if (health.ai?.last_error_type) {
    problems.push({ level: "warn", text: `AI: ${health.ai.last_error_type}` });
  }
  // Somebody who has paid, or is trying to sign up, and cannot proceed until a
  // person acts. It belongs in triage rather than in a card, because it is the
  // only failure here that a customer is currently sitting inside.
  // Payments only: the licence queue is gone with the licence gate, and
  // adding two overlapping counts is what made this number read 5 when the
  // real answer was 2.
  const waiting = health.absence?.waiting_now?.payments || 0;
  if (waiting) {
    problems.push({ level: "bad",
                    text: `${waiting} customer(s) waiting on you` });
  }
  // Ranked, not source-ordered. Unsorted, "6 customers waiting on you" --
  // paying people locked out -- rendered LAST, under three warn-level feeds.
  const rank = { halt: 0, bad: 1, warn: 2 };
  problems.sort((a, b) => (rank[a.level] ?? 3) - (rank[b.level] ?? 3));
  return { problems, worst: problems[0] };
}


// ------------------------------------------------------- the app's own numbers
//
// The first version of this was a gold price chart, which was the wrong answer
// to "where is the chart": the owner can see gold on any website. What he
// cannot see anywhere else is whether HIS product is growing -- who signed up,
// what was published, whether it was delivered, and what the record has done.
//
// Drawn as inline SVG rather than a charting library: these are small bar
// series and a single line, the operator bundle has no business carrying a
// 150KB dependency for that, and an <svg> inherits the theme's tokens for free.

function Spark({ points, colour, height = 44 }) {
  const values = (points || []).map((p) => Number(p.value ?? p.r ?? 0));
  if (values.length < 2) return null;
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = (max - min) || 1;
  const step = 100 / (values.length - 1);
  const path = values
    .map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(2)},${(100 - ((v - min) / span) * 100).toFixed(2)}`)
    .join(" ");
  return html`
    <svg className="stat-spark" viewBox="0 0 100 100" preserveAspectRatio="none"
         style=${{ height: `${height}px` }} aria-hidden="true">
      <path d=${path} fill="none" stroke=${colour} strokeWidth="2"
            vectorEffect="non-scaling-stroke" />
    </svg>`;
}

function Bars({ points, colour, height = 44 }) {
  const values = (points || []).map((p) => Number(p.value || 0));
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const width = 100 / values.length;
  return html`
    <svg className="stat-bars" viewBox="0 0 100 100" preserveAspectRatio="none"
         style=${{ height: `${height}px` }} aria-hidden="true">
      ${values.map((v, i) => {
        // A zero day still draws a hairline. A gap reads as missing data and a
        // flat line reads as a quiet day, and only one of those is the truth.
        const h = v === 0 ? 1.5 : Math.max(3, (v / max) * 100);
        return html`<rect key=${i} x=${(i * width + width * 0.15).toFixed(2)}
                          y=${(100 - h).toFixed(2)}
                          width=${(width * 0.7).toFixed(2)} height=${h.toFixed(2)}
                          fill=${colour} opacity=${v === 0 ? 0.25 : 1} />`;
      })}
    </svg>`;
}

function StatTile({ label, total, note, children }) {
  return html`
    <div className="stat-tile">
      <span className="stat-label">${label}</span>
      <b className="stat-total" dir="ltr">${total}</b>
      ${children}
      ${note && html`<small className="stat-note">${note}</small>`}
    </div>`;
}

function AppStatistics() {
  const [days, setDays] = useState(30);
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    api(`/api/admin/statistics?days=${days}`)
      .then((p) => { if (alive) setState({ loading: false, ...p }); })
      .catch(() => { if (alive) setState({ loading: false, available: false,
                                           reason: "could not reach the server" }); });
    return () => { alive = false; };
  }, [days]);

  const css = typeof getComputedStyle === "function"
    ? getComputedStyle(document.documentElement) : null;
  const pick = (name, fallback) =>
    ((css && css.getPropertyValue(name)) || "").trim() || fallback;

  if (state.loading) {
    return html`<section className="card"><h2>The numbers</h2>
      <p className="muted">Counting…</p></section>`;
  }
  if (!state.available) {
    return html`<section className="card"><h2>The numbers</h2>
      <p className="note-bad" role="status">
        ${state.reason || "The statistics could not be read"}.
      </p></section>`;
  }

  const s = state.series || {};
  const t = state.totals || {};
  return html`
    <section className="card stats">
      <div className="op-chart-head">
        <h2>The numbers</h2>
        <div className="op-frames">
          ${[7, 30, 90].map((d) => html`
            <button key=${d} className=${d === days ? "is-active" : ""}
                    onClick=${() => setDays(d)}>${d}d</button>`)}
        </div>
      </div>

      <div className="stat-grid">
        <${StatTile} label="Signed up" total=${t.signups}>
          <${Bars} points=${s.signups} colour=${pick("--gold", "#C9A227")} /><//>
        <${StatTile} label="Published" total=${t.published}>
          <${Bars} points=${s.published} colour=${pick("--ink-2", "#A39C90")} /><//>
        <${StatTile} label="Alerts delivered" total=${t.delivered}>
          <${Bars} points=${s.delivered} colour=${pick("--ok", "#5E9E7A")} /><//>
        <${StatTile} label="Resolved"
                     total=${t.win_rate != null ? `${t.win_rate}%` : t.resolved}
                     note=${t.win_rate_note}>
          <${Bars} points=${s.resolved} colour=${pick("--ink-3", "#8E887D")} /><//>
      </div>

      ${(state.cumulative_r || []).length > 1 && html`
        <div className="stat-cumulative">
          <span className="stat-label">The record, cumulative R</span>
          <b className="stat-total" dir="ltr">
            ${state.cumulative_r[state.cumulative_r.length - 1].r > 0 ? "+" : ""}${
              state.cumulative_r[state.cumulative_r.length - 1].r}R
          </b>
          <${Spark} points=${state.cumulative_r} height=${72}
                    colour=${state.cumulative_r[state.cumulative_r.length - 1].r >= 0
                      ? pick("--up", "#5E9E7A") : pick("--down", "#BD756D")} />
          <small className="stat-note">
            Every resolved call in the window, added up. It is the product's own
            record and it is not a forecast of the next one.
          </small>
        </div>`}
    </section>`;
}

function ControlRoom({ health }) {
  if (!health) {
    return html`<p className="muted">Reading the system…</p>`;
  }
  const loop = health.loop || {};
  const price = health.price || {};
  // Each block keeps its unread state instead of being flattened into {} --
  // an empty object is indistinguishable from a healthy account with nothing
  // in it, which is exactly how "chain intact · 0 entries" got printed over a
  // record that was never read.
  const record = missing(health.record) ? null : health.record;
  const risk = missing(health.risk) ? null : health.risk;
  const alerts = missing(health.alerts) ? null : health.alerts;
  const chain = record?.chain || {};
  const tick = price.tick_store || {};

  const priceAge = tick.age_s != null ? tick.age_s : null;

  const state = overallState(health);
  const feeds = missing(health.feeds) ? null : health.feeds;
  const absence = missing(health.absence) ? null : health.absence;
  const business = missing(health.business) ? null : health.business;
  const flow = missing(health.flow_today) ? null : health.flow_today;

  return html`
    <div className="stack">
      <section className=${"triage " + (state.worst ? "is-" + state.worst.level : "is-ok")}>
        <b>
          ${!state.worst ? "Everything is healthy"
            : state.worst.level === "halt" ? "PUBLISHING HALTED"
            : state.problems.length === 1 ? "One thing needs you"
            : `${state.problems.length} things need you`}
        </b>
        ${state.problems.length > 0 && html`
          <ul>
            ${state.problems.map((p) => html`
              <li key=${p.text} className=${"is-" + p.level} title=${p.why || ""}>
                ${p.text}</li>`)}
          </ul>`}
      </section>

      ${/* THE HERO. The money used to sit seventh down the page, below the
           row counts of a database table, at the same size as everything
           else -- so the number an owner opens this app to see was the one
           the layout hid best. It leads now, and the only other thing
           allowed beside it is the work that is waiting on a human. */""}
      ${business && html`
        <section className="hero-row">
          <div className=${"hero-money" + ((business.awaiting_payment || 0) ? " has-waiting" : "")}>
            <span className="hero-eyebrow">Per month</span>
            <b className="hero-num">$${(business.monthly_usd || 0).toLocaleString("en")}</b>
            <span className="hero-sub">
              ${business.paying_customers || 0} paying${
                (business.expiring_within_7d || []).length
                  ? ` · ${business.expiring_within_7d.length} end within 7 days` : ""}
            </span>
            ${/* The number this hero could not see. Every customer the
                 operator made by hand came out on no plan, so people with
                 live access were worth $0 here and were routed one style of
                 alerts whatever they paid. Named beside the money, because
                 that is the only place the gap between them shows. */""}
            ${(business.on_no_plan || []).length > 0 && html`
              <a className="hero-gap" href="/admin/customers">
                ${business.on_no_plan.length} with access and no plan →
              </a>`}
          </div>
          ${(business.awaiting_payment || 0) > 0 && html`
            <a className="hero-waiting" href="/admin/website">
              <span className="hero-eyebrow">Waiting on you</span>
              <b className="hero-num">${business.awaiting_payment}</b>
              <span className="hero-sub">confirm a payment →</span>
            </a>`}
        </section>`}

      <${AppStatistics} />

      ${/* SYSTEM HEALTH, FOLDED AWAY.
           These six cards were the dashboard: publishing state, the price
           feed, the chain, subscribers, external feeds and the model, each a
           <dl> of key/value rows, all open at once, above the business. That
           is a diagnostics dump, not a dashboard -- it answers "is every
           subsystem alive" on a screen whose actual question is "how is the
           business and what is the market doing".
           Nothing is removed: the triage banner at the top already names
           anything that needs a human, so the detail belongs one click away
           rather than permanently on screen. */""}
      <details className="op-system">
        <summary>
          <span>System</span>
          <small>${state.problems.length > 0
            ? `${state.problems.length} need${state.problems.length === 1 ? "s" : ""} you`
            : "all healthy"}</small>
        </summary>
    <div className="grid">
      <section className="card">
        <h2>Publishing</h2>
        <div className="metric">
          ${risk ? html`
            <${Light} ok=${risk.publishing_allowed} warn=${false}>
              ${risk.publishing_allowed ? "ALLOWED" : "HALTED"}
            <//>
            ${!risk.publishing_allowed && risk.blocked_by && html`
              <span className="muted">${risk.blocked_by}</span>`}`
            : html`<${NotReported} block=${health.risk} />`}
        </div>
        <dl>
          <div><dt>Kill switch</dt>
               <dd>${risk ? (risk.kill_switch ? "ON" : "off")
                          : html`<${NotReported} block=${health.risk} />`}</dd></div>
          <div><dt>Daemon frame</dt><dd>${loop.daemon_timeframe || "—"}</dd></div>
          <div><dt>Loop</dt><dd>${loop.running ? "running" : "STOPPED"} · ${loop.interval_s || "?"}s</dd></div>
          <div><dt>Today</dt><dd>
            ${flow ? Object.entries(flow)
                       .map(([style, f]) => `${style} ${f.published}/${f.cap}`)
                       .join(" · ")
                   : html`<${NotReported} block=${health.flow_today} />`}
          </dd></div>
        </dl>
      </section>

      <section className="card">
        <h2>Price feed</h2>
        <div className="metric">
          <${Light} ok=${priceAge != null && priceAge < 60} warn=${priceAge != null && priceAge < 300}>
            ${money(price.live_price)}
          <//>
          <span className="muted">${priceAge != null ? `${priceAge}s old` : "no ticks"}</span>
        </div>
        <dl>
          <div><dt>Ticks stored</dt><dd>${tick.ticks ?? "—"}</dd></div>
          <div><dt>Subscribed frames</dt><dd>${(loop.subscribed_timeframes || []).join(", ") || "none"}</dd></div>
        </dl>
      </section>

      <section className="card">
        <h2>Published record</h2>
        ${/* The tamper evidence. An unread chain used to render the words
             "chain intact" beside "0 entries" -- the product's central claim,
             asserted from a block the server never managed to read. */""}
        <div className="metric">
          ${record ? html`
            <${Light} ok=${chain.intact !== false} warn=${false}>
              ${chain.intact === false ? `CHAIN BROKEN @${chain.broken_at}` : "chain intact"}
            <//>
            <span className="muted">${chain.entries ?? 0} entries</span>`
            : html`<${NotReported} block=${health.record}>
                     chain not verified<//>`}
        </div>
        <dl>
          <div><dt>Published</dt><dd>${record ? (record.summary?.published ?? 0) : "—"}</dd></div>
          <div><dt>Resolved</dt><dd>${record ? (record.summary?.resolved ?? 0) : "—"}</dd></div>
          <div><dt>Open</dt><dd>${record ? (record.summary?.open ?? 0) : "—"}</dd></div>
        </dl>
      </section>

      <section className="card">
        <h2>Alerts</h2>
        <div className="metric">
          ${alerts ? html`
            <${Light} ok=${(alerts.subscribers || 0) > 0} warn=${alerts.configured}>
              ${alerts.subscribers ?? 0} ${(alerts.subscribers || 0) === 1 ? "subscriber" : "subscribers"}
            <//>`
            : html`<${NotReported} block=${health.alerts} />`}
        </div>
        ${alerts && (alerts.subscribers || 0) === 0 && html`
          <p className="muted">
            Zero subscribers means no alert can be delivered no matter what the
            engine finds. Web Push requires HTTPS — over plain http the browser
            refuses to register at all.
          </p>`}
      </section>


      <section className="card">
        <h2>External feeds</h2>
        ${!feeds && html`<p className="note-bad">
          The feed health could not be read — nothing below is current.</p>`}
        ${feeds && Object.keys(feeds).length === 0 && html`<p className="muted">Not reported.</p>`}
        <ul className="feed-list">
          ${Object.entries(feeds || {}).map(([key, f]) => html`
            <li key=${key}>
              <span>
                <${Light} ok=${!f.backing_off} warn=${false}>${f.name}<//>
                <small>${f.backing_off ? "fetch failed — not retrying yet" : f.note}</small>
              </span>
              <${FeedRefresh} feed=${key} />
            </li>`)}
        </ul>
        <p className="muted">
          The calendar gates trading, FRED drives the macro filter, and COT is
          on the customer dashboard. "Down" means a fetch failed and we are
          deliberately not retrying yet.
        </p>
      </section>

      ${/* The business, in numbers. Until now the operator could see who had
           NOT paid and nothing else -- the moment a payment was confirmed the
           subscription left every screen, so "how much is coming in" and "who
           is about to lapse" had no answer anywhere in the product. */""}
      </div>
      </details>

    <div className="grid">
      ${!business && html`
        <section className="card">
          <h2>The business</h2>
          <div className="metric"><${NotReported} block=${health.business} /></div>
          <p className="muted">
            The money is not a number this screen may guess at. Nothing is
            shown here until the server can read the subscriptions again.
          </p>
        </section>`}

      ${business && html`
        <section className="card">
          <h2>The business</h2>
          <div className="metric">
            <b className="biz-mrr">$${(business.monthly_usd || 0).toLocaleString("en")}</b>
            <span className="muted">per month · ${business.paying_customers || 0} paying</span>
          </div>
          <dl className="kv">
            ${(business.by_plan || []).map((p) => html`
              <div key=${p.plan}><dt>${p.plan}</dt>
                <dd>${p.customers} × $${(p.monthly || 0).toLocaleString("en")}</dd></div>`)}
            ${(business.by_plan || []).length === 0 && html`
              <div><dt>active</dt><dd>none yet</dd></div>`}
            <div><dt>awaiting payment</dt>
              <dd className=${business.awaiting_payment ? "warn" : ""}>${business.awaiting_payment}</dd></div>
            ${(business.lapsed_unrenewed || 0) > 0 && html`
              <div><dt>lapsed, still on a plan</dt>
                <dd className="warn">${business.lapsed_unrenewed}</dd></div>`}
          </dl>
          ${(business.on_no_plan || []).length > 0 && html`
            <div className="biz-expiring">
              <b>Access, but on no plan</b>
              <ul className="feed-list">
                ${business.on_no_plan.map((u) => html`
                  <li key=${u.username}><span><b>${u.username}</b>
                    <small>${u.expires_at || "no end date"}</small></span></li>`)}
              </ul>
              <p className="muted">
                They can sign in and they carry no subscription, so the router
                sends them ONE style whatever they paid and none of them is
                counted in the money above. Set each one's plan from Customers.
              </p>
            </div>`}
          ${(business.expiring_within_7d || []).length > 0 && html`
            <div className="biz-expiring">
              <b>Ends within 7 days</b>
              <ul className="feed-list">
                ${business.expiring_within_7d.map((u) => html`
                  <li key=${u.username}><span><b>${u.username}</b>
                    <small>${u.expires_at}</small></span></li>`)}
              </ul>
            </div>`}
          <p className="muted">${business.note}</p>
        </section>`}

      ${!absence && html`
        <section className="card absence">
          <h2>The absence test</h2>
          <div className="metric"><${NotReported} block=${health.absence} /></div>
          <p className="muted">
            "The product could have run without anyone" is a claim about a
            week of evidence. With the evidence unread it is not a smaller
            claim, it is no claim at all.
          </p>
        </section>`}

      ${absence && html`
        <section className=${"card absence is-" + absence.verdict}>
          <h2>The absence test</h2>
          ${/* One operator to a thousand customers is arithmetic, not
               ambition, and what breaks it is never a big task -- it is a
               small one that must happen every day. This counts the actions
               only a human could have taken, and names the next one to
               automate so the decision is not a debate. */""}
          <p className="absence-headline">${absence.headline}</p>
          <ul className="absence-list">
            ${(absence.tasks || []).map((task) => html`
              <li key=${task.key} className=${task.blocking ? "is-blocking" : ""}>
                <span>
                  <b>${task.what}</b>
                  <small>${task.if_absent}</small>
                </span>
                <span className="absence-count">${task.this_week}<small>/wk</small></span>
              </li>`)}
          </ul>
          ${absence.next_to_automate && html`
            <p className="muted">
              Next to automate: <b>${absence.next_to_automate}</b> —
              ${(absence.tasks.find((t) => t.key === absence.next_to_automate) || {}).automation}
            </p>`}
          <p className="muted">${absence.goal}</p>
        </section>`}


      <section className="card">
        <h2>AI</h2>
        <div className="metric">
          <${Light} ok=${health.ai?.enabled && !health.ai?.last_error_type} warn=${health.ai?.enabled}>
            ${health.ai?.enabled ? (health.ai.last_error_type || "healthy") : "DISABLED"}
          <//>
          <span className="muted">${health.ai?.model || ""}</span>
        </div>
        ${health.ai?.last_error_message && html`
          <p className="muted">${health.ai.last_error_message}</p>`}
      </section>
    </div>
    </div>`;
}

// Is each filter still doing anything? (src/trading/filter_power)
//
// A filter is a claim: trades taken when this holds do better than trades
// taken when it does not. That claim stops being true quietly. The macro
// filter here was measured on a nine-year sample and then vetoed trades for
// years with nobody checking — which is how a strategy decays instead of
// failing loudly, and it is the operator's job to see it.
//
// A filter with too few observations on one side reports NOTHING. It is the
// row most likely to be believed and the one least entitled to be.
function FilterPower() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const payload = await api("/api/admin/filter-power");
        if (alive && payload && payload.success) setData(payload);
      } catch (e) { /* the panel simply does not appear */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!data) return null;

  return html`
    <section className="card">
      <h2>Are the filters still working?</h2>
      ${!data.available
        ? html`<p className="muted">${data.reason}</p>`
        : html`
          <p className="muted">${data.note}</p>
          <table className="admin-table">
            <thead><tr>
              <th>Style</th><th>Filter</th>
              <th className="num">Allowed</th><th className="num">Blocked</th>
              <th className="num">Delta R</th><th>Verdict</th>
            </tr></thead>
            <tbody>
              ${data.filters.map((f) => html`
                <tr key=${f.style + f.filter}
                    className=${f.verdict === "dead" ? "is-dead"
                              : f.verdict === "weak" ? "is-weak" : ""}>
                  <td>${f.style}</td>
                  <td>${f.label || f.filter}</td>
                  <td className="num">${f.allowed == null ? "—" : f.allowed}</td>
                  <td className="num">${f.blocked == null ? "—" : f.blocked}</td>
                  ${/* A filter that could not be measured shows a dash, never
                       a zero. Zero delta means "measured, and it does
                       nothing"; that is a different fact from "not enough
                       trades on one side to say". */""}
                  <td className="num">${f.delta_r == null ? "—"
                    : (f.delta_r > 0 ? "+" : "") + f.delta_r}</td>
                  <td>${f.available === false
                    ? html`<small className="muted">not enough trades</small>`
                    : f.verdict}</td>
                </tr>`)}
            </tbody>
          </table>
          ${data.measured_on && html`
            <small className="muted">Measured on ${data.measured_on}</small>`}`}
    </section>`;
}

// The fund's pulse and its shadow candidate, on screen instead of over SSH.
//
// Everything here was already true in the database — heartbeats, shadow
// signals, gate rejections, risk clamps — but the operator was reading it
// with sqlite3 and journalctl, which means in practice nobody read it.
// The stream is append-only; this card renders it and can change nothing.
function FundEventsCard() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const payload = await api("/api/admin/fund-events");
        if (alive && payload && payload.success) setData(payload);
        else if (alive) setFailed(true);
      } catch (e) { if (alive) setFailed(true); }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  if (failed) return html`
    <section className="card"><h2>Fund events</h2>
      <p className="muted">Not reported.</p></section>`;
  if (!data) return null;

  const sh = data.shadow || {};
  const events = data.events || [];
  const lastBeat = events.find((e) => e.kind === "HEARTBEAT");

  return html`
    <section className="card">
      <h2>Fund events <small className="muted">append-only</small></h2>
      <div className="stat-row">
        <div><b>${sh.signals ?? 0} / ${sh.needed_for_packet ?? 30}</b>
          <small>shadow signals (${sh.pod || "—"})</small></div>
        <div><b>${sh.resolved ?? 0}</b><small>resolved
          ${sh.realized_r ? ` (${sh.realized_r > 0 ? "+" : ""}${sh.realized_r}R)` : ""}</small></div>
        <div><b>${sh.open ?? 0}</b><small>open now</small></div>
        <div><b>${lastBeat ? fmtSeen(lastBeat.at) : "none yet"}</b>
          <small>last heartbeat</small></div>
      </div>
      ${events.length === 0
        ? html`<p className="muted">No events yet — the first heartbeat
            arrives within 30 minutes of the daemon starting.</p>`
        : html`
          <table className="admin-table">
            <thead><tr><th>When</th><th>Event</th><th>Pod</th><th>Detail</th></tr></thead>
            <tbody>
              ${events.slice(0, 25).map((e) => html`
                <tr key=${e.id}
                    className=${["WEEKLY_HALT", "BROKER_DISCONNECTED", "CLAMP"]
                      .includes(e.kind) ? "is-weak" : ""}>
                  <td>${fmtSeen(e.at)}</td>
                  <td>${e.kind}</td>
                  <td>${e.pod || "—"}</td>
                  <td><small className="muted">
                    ${e.kind === "HEARTBEAT"
                      ? `daily ${e.payload?.daily_r ?? "—"}R · open ${e.payload?.open_signals ?? "—"} · price age ${e.payload?.price_age_s ?? "—"}s`
                      : e.kind === "SHADOWED"
                      ? `${e.payload?.side} @ ${e.payload?.entry} · stop ${e.payload?.stop} · target ${e.payload?.target}`
                      : e.kind === "GATE_REJECTED"
                      ? `${e.payload?.reason}${e.payload?.side ? " · " + e.payload.side : ""}${e.payload?.timeframe ? " · " + e.payload.timeframe : ""}`
                      : JSON.stringify(e.payload || {}).slice(0, 90)}
                  </small></td>
                </tr>`)}
            </tbody>
          </table>`}
    </section>`;
}

function RecordScreen({ record }) {
  if (!record) return html`<p className="muted">…</p>`;
  const signals = record.signals || [];
  const closed = record.trades?.closed || [];
  const open = record.trades?.open || [];

  // The three states, counted. The operator's first question about the record
  // is how much of it has actually answered, and the header is where he looks.
  const tally = { won: 0, lost: 0, pending: 0, flat: 0 };
  signals.forEach((s) => { tally[resultOf(s).key] += 1; });
  const trades = [...open, ...closed];
  const tradeTally = { won: 0, lost: 0, pending: 0, flat: 0 };
  trades.forEach((t) => { tradeTally[tradeResultOf(t).key] += 1; });

  return html`
    <div className="stack">
      <section className="card">
        <h2>Published signals <small className="zone-note">times in ${OPERATOR_ZONE}</small> <span className="muted">${signals.length}${signals.length
          ? ` · ${tally.won} won · ${tally.lost} lost · ${tally.pending} no result yet`
          : ""}${tally.flat ? ` · ${tally.flat} break-even` : ""}</span></h2>
        ${signals.length === 0 && html`<p className="muted">Nothing published yet.</p>`}
        <div className="table-wrap">
          <table className="stacks">
            <thead><tr><th>when</th><th>style</th><th>side</th><th>tf</th><th>entry</th><th>stop</th>
                       <th>target</th><th>R:R</th><th>result</th><th>outcome</th><th>exit</th><th>R</th>
                       <th>held</th><th>engine</th><th>feed</th><th>what it saw</th></tr></thead>
            <tbody>
              ${signals.map((s) => html`
                <tr key=${s.signal_id} className=${s.verdict ? "has-verdict" : ""}>
                  <td data-label="when">${fmtSeen(s.published_at)}</td>
                  ${/* WHICH PROFILE published this. The frame identifies the
                       style -- each style publishes on its own home frame --
                       and the operator's first question about any call is
                       which product it belongs to. */""}
                  <td data-label="style">${styleOf(s.timeframe)}</td>
                  ${/* Direction is a CATEGORY, not a verdict. It used to
                       render in the same green and red as the result, so one
                       row showed three green/red columns meaning two
                       different things. Colour in this table now means won or
                       lost, and nothing else. */""}
                  <td data-label="side" className="side-cell">${s.side}</td>
                  <td data-label="tf">${s.timeframe || "—"}</td>
                  <td data-label="entry">${money(s.entry)}</td>
                  <td data-label="stop">${money(s.stop)}</td>
                  <td data-label="target">${money(s.target)}</td>
                  <td data-label="R:R">${s.rr != null ? Number(s.rr).toFixed(2) : "—"}</td>
                  ${/* WON, LOST, or NO RESULT YET -- the one column that
                       answers the operator's first question without being
                       read as a sentence. The prose in `outcome` explains
                       it; this states it. */""}
                  <td data-label="result" className=${resultOf(s).cls}
                      title=${resultOf(s).why}>${resultOf(s).word}</td>
                  ${/* The OUTCOME in words, derived from the prices -- never
                       the raw status. "expired" is not a thing that happens to
                       a trade: a call whose entry never traded was never
                       opened, and a call closed by its window touched neither
                       level. Both used to read as one flat word. */""}
                  <td data-label="outcome" className=${outcomeClass(s)}>${outcomeOf(s)}</td>
                  <td data-label="exit">${s.exit_price != null ? money(s.exit_price) : "—"}</td>
                  <td data-label="R" className=${s.r_multiple > 0 ? "pos" : (s.r_multiple < 0 ? "neg" : "")}>
                    ${s.r_multiple != null ? Number(s.r_multiple).toFixed(2) : "—"}
                  </td>
                  <td data-label="held">${heldFor(s)}</td>
                  <td data-label="engine">${s.engine || "—"}</td>
                  <td data-label="feed">${s.price_feed || "—"}</td>
                  ${/* What the engine actually saw when it published this. A
                       resolved signal could always be scored and never
                       explained; this is where the frozen verdict earns its
                       column. */""}
                  <td data-label="saw" className="v-saw">
                    ${s.verdict ? html`
                      <span title=${JSON.stringify(s.verdict, null, 1)}>
                        ${s.verdict.regime || "—"}
                        ${s.verdict.scores
                          ? ` · ${s.verdict.scores.buy ?? "?"}/${s.verdict.scores.sell ?? "?"}`
                          : ""}
                        ${s.verdict.adx != null ? ` · ADX ${Number(s.verdict.adx).toFixed(0)}` : ""}
                        ${(s.verdict.blockers || []).length
                          ? ` · ${s.verdict.blockers.length} blocker(s)` : ""}
                      </span>` : html`<span className="muted">not recorded</span>`}
                  </td>
                </tr>`)}
            </tbody>
          </table>
        </div>
      </section>

      ${/* THE SIMULATION, beside what actually happened -- and never mixed
             into it. The live ledger holds a handful of signals; the backtest
             holds thousands, and the operator's first question about the
             handful is whether it is behaving like the simulation. Summing or
             averaging the two would be the single worst thing this screen
             could do, so they are separate cards with different words. */""}
      ${record.backtest && html`
      <section className="card">
        <h2>Backtest <small className="zone-note">
          a simulation, not the record${record.backtest.generated_at
            ? ` · measured ${record.backtest.generated_at}` : ""}</small></h2>
        <p className="muted">${record.backtest.costs}</p>
        <div className="table-wrap">
          <table className="stacks">
            <thead><tr><th>style</th><th>window</th><th>trades</th>
                       <th>wins per 10</th><th>needs per 10</th><th>expectancy</th>
                       <th>interval</th><th>verdict</th><th>reaches target</th></tr></thead>
            <tbody>
              ${record.backtest.rows.map((r) => {
                const clear = r.win_rate - r.breakeven_realized;
                const tp = (r.exit_mix_pct || {}).take_profit;
                return html`
                <tr key=${`${r.style}-${r.window}`}>
                  <td data-label="style">${r.style}</td>
                  <td data-label="window">${r.window}</td>
                  <td data-label="trades" className="num">${Number(r.trades).toLocaleString("en")}</td>
                  ${/* The number the owner asked for, in the unit he asked for.
                       Beside it, ALWAYS, the number it has to clear -- a win
                       rate shown alone is how a style under water reads as an
                       edge. */""}
                  <td data-label="wins per 10" className="num">
                    ${(r.win_rate / 10).toFixed(1)}</td>
                  <td data-label="needs per 10" className="num">
                    ${(r.breakeven_realized / 10).toFixed(1)}</td>
                  <td data-label="expectancy"
                      className=${`num ${r.expectancy_r > 0 ? "res-won" : "res-lost"}`}>
                    ${r.expectancy_r > 0 ? "+" : ""}${Number(r.expectancy_r).toFixed(4)}</td>
                  <td data-label="interval" className="num">
                    [${Number(r.ci_low).toFixed(3)}, ${Number(r.ci_high).toFixed(3)}]</td>
                  ${/* Decisive means the interval excludes zero. Everything
                       else is "we cannot tell", and that is what it says. */""}
                  <td data-label="verdict"
                      className=${r.decisive
                        ? (r.ci_low > 0 ? "res-won" : "res-lost") : "res-pending"}>
                    ${r.decisive ? (r.ci_low > 0 ? "decisive" : "decisively negative")
                                 : `${clear > 0 ? "+" : ""}${clear.toFixed(1)} pts, not decisive`}</td>
                  <td data-label="reaches target" className="num">
                    ${tp == null ? "—" : `${tp}%`}</td>
                </tr>`;
              })}
            </tbody>
          </table>
        </div>
        <p className="muted">${record.backtest.note}</p>
      </section>`}

      <section className="card">
        <h2>Trades <small className="zone-note">times in ${OPERATOR_ZONE}</small> <span className="muted">${open.length} open · ${closed.length} closed${
          trades.length ? ` · ${tradeTally.won} won · ${tradeTally.lost} lost · ${tradeTally.pending} no result yet` : ""
        }${tradeTally.flat ? ` · ${tradeTally.flat} break-even` : ""}</span></h2>
        <div className="table-wrap">
          <table className="stacks">
            <thead><tr><th>opened</th><th>user</th><th>side</th><th>tf</th><th>entry</th>
                       <th>exit</th><th>result</th><th>why</th><th>R</th><th>risked</th><th></th></tr></thead>
            <tbody>
              ${trades.map((t) => html`
                <tr key=${t.id} className=${t.excluded ? "is-excluded" : ""}>
                  <td data-label="opened">${fmtSeen(t.opened_at)}</td>
                  <td data-label="user">${t.user_id ?? "—"}</td>
                  <td data-label="side" className="side-cell">${t.side}</td>
                  <td data-label="tf">${t.timeframe || "—"}</td>
                  <td data-label="entry">${money(t.entry_price)}</td>
                  <td data-label="exit">${t.exit_price != null ? money(t.exit_price) : "open"}</td>
                  <td data-label="result" className=${tradeResultOf(t).cls}
                      title=${tradeResultOf(t).why}>${tradeResultOf(t).word}</td>
                  <td data-label="why">${t.exit_reason || "—"}</td>
                  <td data-label="R" className=${t.r_multiple > 0 ? "pos" : (t.r_multiple < 0 ? "neg" : "")}>
                    ${t.r_multiple != null ? Number(t.r_multiple).toFixed(2) : "—"}
                  </td>
                  ${/* What was actually risked. Without it the record reads
                       only in R, and every prop-firm rule -- and every
                       customer question about money -- is in dollars. A trade
                       with no size is named, not blanked: it is the reason a
                       floor cannot be computed for that account. */""}
                  <td data-label="risked">
                    ${t.risk_amount != null
                      ? html`<span>$${Number(t.risk_amount).toFixed(0)}
                               <small className="muted">${t.lot_size ? ` · ${t.lot_size} lot` : ""}</small></span>`
                      : html`<span className="muted">no size</span>`}
                  </td>
                  <td data-label="flags">${t.source === "manual_override" ? "override" : ""}
                      ${t.excluded ? " · excluded" : ""}</td>
                </tr>`)}
            </tbody>
          </table>
        </div>
        ${closed.length === 0 && open.length === 0 && html`
          <p className="muted">No trades recorded.</p>`}
      </section>
    </div>`;
}

function AuditScreen({ actions }) {
  if (!actions) return html`<p className="muted">…</p>`;
  return html`
    <section className="card">
      <h2>Operator actions <small className="zone-note">times in ${OPERATOR_ZONE}</small> <span className="muted">${actions.length}</span></h2>
      ${actions.length === 0 && html`<p className="muted">Nothing recorded yet.</p>`}
      <div className="table-wrap">
        <table className="stacks">
          <thead><tr><th>when</th><th>who</th><th>action</th><th>target</th><th>result</th></tr></thead>
          <tbody>
            ${actions.map((a) => html`
              <tr key=${a.id}>
                <td data-label="when">${fmtSeen(a.at)}</td>
                <td data-label="who">${a.actor || a.actor_id || "—"}</td>
                <td data-label="action">${a.action}</td>
                <td data-label="target">${a.target || "—"}</td>
                <td data-label="result">${a.result}</td>
              </tr>`)}
          </tbody>
        </table>
      </div>
    </section>`;
}

function ServicesScreen() {
  const [features, setFeatures] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  // Loading and failed are different states and this screen had only one.
  const [failed, setFailed] = useState(null);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const r = await api("/api/admin/features");
      // Guarded, like the per-customer panel already was: this took
      // r.features from ANY body, so a refusal stored undefined and left the
      // screen on its loading ellipsis for good.
      if (!r || !r.features) {
        setFailed((r && (r.detail || r.error))
          ? `The server refused to list the services: ${r.detail || r.error}`
          : "The server did not return the service switches.");
        return;
      }
      setFeatures(r.features);
      setNote(null);
    } catch (e) {
      setFailed(e.message === "signed-out" || e.message === "not-operator"
        ? "This session is not an operator's any more."
        : "Could not read the service switches — the request did not reach "
          + "the server.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (key, meta) => {
    const next = !meta.enabled;
    let reason = null;
    if (!next) {
      // The customer app shows this sentence in place of the feature, so the
      // operator is writing to users, not to a log.
      reason = window.prompt(
        `Switch "${meta.label}" off for EVERY customer.\n\n` +
        "What should they be told? (shown in the app where the feature was)",
        "Temporarily unavailable — we're working on it.");
      if (reason === null) return;
    }
    setBusy(key);
    try {
      const r = await api("/api/admin/features", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feature: key, enabled: next, reason }),
      });
      if (!r || !r.features) {
        setNote((r && (r.detail || r.error))
          ? `That switch did not take: ${r.detail || r.error}`
          : "That switch did not take. Nothing changed.");
        setBusy(null);
        return;
      }
      setFeatures(r.features);
      setNote(`${meta.label} is now ${next ? "on" : "off"} for everyone.`);
    } catch (e) { setNote("The switch did not take — nothing changed."); }
    setBusy(null);
  }, []);

  // The whole screen used to collapse to a bare "…" here, forever, on a
  // dropped connection: the message was written into `note` and this line
  // returned before anything could render it. An ellipsis with no way
  // forward is not a loading state, it is a dead screen.
  if (!features) {
    return html`
      <section className="card">
        <h2>Services</h2>
        ${failed
          ? html`
            <p className="note-bad" role="alert">${failed}</p>
            <p className="muted">
              Nothing was changed. The switches below are not shown because
              this screen does not know which way they are set.
            </p>
            <button onClick=${load}>Try again</button>`
          : html`<p className="muted">Reading the service switches…</p>`}
      </section>`;
  }
  const keys = Object.keys(features);
  const off = keys.filter((k) => !features[k].enabled);
  return html`
    <div className="stack">
      <section className="card">
        <h2>Services</h2>
        <p className="op-hint">
          Off here means off for every customer, with no per-customer exception.
          A user switched off individually stays off when you switch a service
          back on. Switching one off asks you for a sentence, and that sentence
          is printed in the app where the feature used to be — so it is written
          to customers, not to a log.
        </p>
        ${off.length > 0 && html`
          <p className="metric neg">${off.length} service(s) are down for everyone.</p>`}
        <ul className="feature-list">
          ${keys.map((k) => html`
            <li key=${k} className=${features[k].enabled ? "" : "is-off"}>
              <div className="feature-head">
                <${Light} ok=${features[k].enabled} warn=${false}>
                  ${features[k].label}
                <//>
                <button disabled=${busy === k} onClick=${() => toggle(k, features[k])}
                        className=${features[k].enabled ? "btn-warn" : ""}>
                  ${busy === k ? "…" : features[k].enabled ? "switch off" : "switch on"}
                </button>
              </div>
              <p className="muted">${features[k].note}</p>
              ${!features[k].enabled && features[k].reason && html`
                <p className="feature-reason">Users see: “${features[k].reason}”</p>`}
            </li>`)}
        </ul>
      </section>
      ${note && html`<p className="note-ok" role="status">${note}</p>`}
    </div>`;
}


// A panel that opens takes the screen with it.
//
// At 200 customers the table is 19,000px tall, and clicking "view" on the
// last row opened the panel at the TOP of that page -- 36,000px from the
// finger that asked for it, with no scroll and no focus move. The operator
// clicked, nothing appeared to happen, and the answer was two screens' worth
// of scrolling above them.
//
// Closing had the mirror defect: focus fell to BODY, so a keyboard operator
// who closed a panel was returned to the very start of the document.
function useOpenedPanel(focusSelector, openerRef) {
  const ref = useRef(null);
  useEffect(() => {
    // The control that opened this, remembered by the screen at CLICK time.
    // document.activeElement is not good enough on its own: a window.confirm
    // in the middle of the action blurs the button, so by the time the panel
    // mounts the "opener" is BODY -- and closing then dropped the keyboard at
    // the top of the document, which is the defect this exists to fix.
    const opener = (openerRef && openerRef.current) || document.activeElement;
    const el = ref.current;
    if (el) {
      el.scrollIntoView({ block: "start" });
      // A form gets its first field; a read-only panel gets the panel itself,
      // which is why the sections carry tabIndex="-1".
      const target = (focusSelector && el.querySelector(focusSelector)) || el;
      // preventScroll: the scrollIntoView above already chose the position;
      // the focus call must not fight it.
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
    return () => {
      // Back to the control that opened it -- unless the row it lived in is
      // gone, in which case anywhere is better than nowhere.
      try {
        if (opener && opener.focus && document.contains(opener)) opener.focus();
      } catch (e) { /* focus is best-effort, never a crash */ }
    };
  }, [focusSelector, openerRef]);
  return ref;
}

// One customer, whole.
//
// The operator's most common question is about a person -- "what is this
// account actually seeing, and why are they complaining" -- and answering it
// meant reading four screens and joining them by hand. Everything that
// determines what this person experiences, in one place.
function CustomerDetail({ user, onClose, opener }) {
  const [data, setData] = useState(null);
  const panel = useOpenedPanel(null, opener);

  useEffect(() => {
    // Keyed on user.id, so this effect re-runs when the operator switches
    // customers. Without the guard the SLOWER of two in-flight reads wins,
    // and customer A's plan, balance and history render under customer B's
    // name -- the one screen where being wrong about who you are looking at
    // decides what the operator charges them.
    let alive = true;
    setData(null);
    api(`/api/admin/customer/${user.id}`)
      .then((p) => { if (alive) setData(p.customer || { failed: true, why: p && p.error }); })
      .catch((e) => { if (alive) setData({ failed: true, why: e.message }); });
    return () => { alive = false; };
  }, [user.id]);

  if (!data) {
    return html`
      <section className="card" ref=${panel} tabIndex="-1">
        <p className="muted">Reading ${user.username}…</p>
      </section>`;
  }
  if (data.failed) {
    return html`
      <section className="card" ref=${panel} tabIndex="-1">
        <p className="note-bad" role="alert">
          Could not read this customer${data.why === "not_found"
            ? " — there is no account with that id." : "."}
        </p>
        <button onClick=${onClose}>Close</button>
      </section>`;
  }

  // Each block can be unreadable on its own, and an unreadable block is NOT
  // an empty one: /customer/<id> used to answer 200 with every default in
  // place for an id that did not exist, so "no trades, every service on,
  // balance not set" was printed with the same confidence for a real quiet
  // account, a failed read, and a person who was never there.
  const prefs = missing(data.preferences) ? null : data.preferences;
  const alerts = missing(data.alerts) ? null : data.alerts;
  const trading = missing(data.trading) ? null : data.trading;
  const stats = (trading && trading.stats) || {};
  const discipline = stats.discipline || {};
  const services = missing(data.services) ? null : data.services;
  const off = Object.entries((services || {}).effective || {})
    .filter(([, on]) => on === false).map(([k]) => k);
  const account = missing(data.account) ? null : data.account;

  return html`
    <section className="card" ref=${panel} tabIndex="-1">
      <h2>${user.username}</h2>

      ${!prefs ? html`
        <p className="note-bad">Their preferences could not be read.</p>` : html`
        <dl>
          <div><dt>Style</dt><dd>${prefs.trading_style || "—"} · ${prefs.timeframe || "—"}</dd></div>
          <div><dt>Balance / risk</dt>
               <dd>${prefs.account_balance ? "$" + prefs.account_balance : "not set"}
                   ${prefs.risk_percent ? " · " + prefs.risk_percent + "%" : ""}</dd></div>
          <div><dt>Language</dt><dd>${prefs.language || "en"}</dd></div>
        </dl>`}

      <h3>Will they receive an alert?</h3>
      ${!alerts ? html`
        <p className="note-bad">Their alert delivery could not be read.</p>` : html`
        <dl>
          <div><dt>Subscribed device</dt>
               <dd><${Light} ok=${!!alerts.subscribed} warn=${false}>
                 ${alerts.subscribed ? "yes" : "no — nothing can reach them"}<//></dd></div>
          <div><dt>Watching levels</dt><dd>${alerts.watching ?? 0}</dd></div>
          ${alerts.policy && html`
            <${React.Fragment}>
              <div><dt>Quiet hours</dt>
                   <dd>${alerts.policy.quiet_from != null
                     ? `${alerts.policy.quiet_from}:00 → ${alerts.policy.quiet_to}:00`
                     : "none"}</dd></div>
              <div><dt>Daily cap</dt><dd>${alerts.policy.daily_cap ?? "none"}</dd></div>
              <div><dt>Types off</dt>
                   <dd>${Object.entries(alerts.policy.types || {})
                     .filter(([, on]) => on === false).map(([k]) => k).join(", ") || "none"}</dd></div>
            <//>`}
        </dl>`}

      <h3>Their record</h3>
      ${!trading ? html`
        <p className="note-bad">Their trades could not be read.</p>` : html`
        <dl>
          <div><dt>Trades</dt><dd>${trading.open ?? 0} open · ${trading.closed ?? 0} closed</dd></div>
          <div><dt>Discipline delta</dt>
               <dd>${discipline.delta_r != null ? discipline.delta_r + "R" : "not enough sample"}</dd></div>
          ${trading.unpriced > 0 && html`
            <div><dt>Unpriced trades</dt>
                 <dd className="neg">${trading.unpriced} — no lot size recorded</dd></div>`}
        </dl>`}

      ${missing(data.services) && html`
        <p className="note-bad">Their services could not be read.</p>`}

      ${account && html`
        <${React.Fragment}>
          <h3>Funded account</h3>
          <dl>
            <div><dt>Kind</dt><dd>${account.kind}</dd></div>
            <div><dt>Daily limit</dt><dd>${account.daily_loss_limit ?? "none"}</dd></div>
            <div><dt>Overall</dt><dd>${account.max_loss ?? "none"}</dd></div>
          </dl>
        <//>`}

      ${off.length > 0 && html`
        <p className="muted">
          Services switched off for this account: <b>${off.join(", ")}</b>.
          They will see the "switched off" screen where those would be.
        </p>`}

      <button onClick=${onClose}>Close</button>
    </section>`;
}

function CustomerServices({ user, onClose, opener }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const panel = useOpenedPanel(null, opener);
  // A refusal has to land somewhere the operator can see it. This panel had
  // nowhere at all: a rejected toggle stored `undefined` and the next
  // Object.keys(features) took the whole React root down to a white screen.
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try { setState(await api(`/api/admin/features?user_id=${user.id}`)); }
    catch (e) { setState(null); }
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const set = useCallback(async (key, value) => {
    setBusy(key);
    try {
      const r = await api("/api/admin/features", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feature: key, enabled: value, user_id: user.id }),
      });
      // Guarded. This assigned regardless of r.success, so a refused toggle
      // stored `undefined` and the next Object.keys(features) threw -- with
      // no error boundary under the root, the entire operator app went blank
      // and the only way back was knowing to reload.
      if (!r || !r.features) {
        setNote((r && (r.detail || r.error))
          ? `That switch did not take: ${r.detail || r.error}`
          : "That switch did not take. Nothing changed.");
        setBusy(null);
        return;
      }
      setNote(null);
      setState({ features: r.features, user: r.user });
    } catch (e) {
      // "The panel simply does not move" was the old comment here, and a
      // control that does nothing and says nothing is indistinguishable from
      // one that worked.
      setNote("The switch did not reach the server. Nothing changed.");
    }
    setBusy(null);
  }, [user.id]);

  if (!state) {
    return html`
      <section className="card" ref=${panel} tabIndex="-1">
        <p className="muted">Reading ${user.username}'s services…</p>
      </section>`;
  }
  const { features, user: u } = state;
  return html`
    <section className="card" ref=${panel} tabIndex="-1">
      ${note && html`<p className="note-bad" role="status">${note}</p>`}
      <h2>What ${user.username} can use</h2>
      <p className="muted">
        “default” follows the service switch. “on”/“off” is a decision about
        this customer only — and cannot re-open a service that is off for
        everyone.
      </p>
      <ul className="feature-list">
        ${Object.keys(features).map((k) => {
          const override = u.overrides[k];
          const chosen = override === undefined ? "default" : override ? "on" : "off";
          const globallyOff = !features[k].enabled;
          return html`
            <li key=${k} className=${u.effective[k] ? "" : "is-off"}>
              <div className="feature-head">
                <${Light} ok=${u.effective[k]} warn=${false}>${features[k].label}<//>
                <span className="seg" role="group">
                  ${[["default", null], ["on", true], ["off", false]].map(([label, value]) => html`
                    <button key=${label} disabled=${busy === k}
                            className=${chosen === label ? "is-active" : ""}
                            onClick=${() => set(k, value)}>${label}</button>`)}
                </span>
              </div>
              ${globallyOff && html`
                <p className="muted">Off for everyone right now, so this has no effect yet.</p>`}
            </li>`;
        })}
      </ul>
      <button onClick=${onClose}>Done</button>
    </section>`;
}

// "YYYY-MM-DD HH:MM" in the operator's own zone -- one date language across
// the whole product, no seconds noise, no 8/11-vs-11/8 ambiguity.
function fmtSeen(value) {
  if (!value) return "never";
  // Accepts BOTH an ISO string (users.last_login) and unix SECONDS (the
  // ledger's published_at / resolved_at). Passing seconds to new Date()
  // treats them as milliseconds and renders 1970, which is exactly what the
  // record table started doing the moment it began using this helper.
  const d = typeof value === "number"
    ? new Date(value < 1e12 ? value * 1000 : value)
    : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-CA") + " " +
         d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

// The operator's own zone, named once so a table can say which clock it is on.
//
// This console rendered THREE date formats on three tabs and two of them were
// on different clocks. Signals used fmtSeen (local). Trades sliced the raw ISO
// string and printed UTC unconverted. Audit used toLocaleString() with no
// locale at all, so it produced "8/14/2026, 2:07:29 PM" -- US order, twelve
// hour, with seconds -- and would have flipped to Eastern-Arabic digits the
// moment the console got an Arabic lang attribute.
//
// The first two look identical and sit one card apart on the same screen. For
// an operator at UTC+3 the trades table ran three hours behind the signals
// table with nothing on the page to say so. In a trading console that is a
// correctness defect, not a cosmetic one.
const OPERATOR_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";
  } catch (e) {
    return "local time";
  }
})();

// The ladder, read from the server instead of typed in here.
//
// src/billing/plans.py is where a tier's price and its style coverage are
// defined; the alert router and the money numbers both read it. A copy of the
// list in this file would be another place "$500" is written down, and the
// first one to go stale -- so the picker asks.
function usePlans() {
  const [plans, setPlans] = useState(null);
  useEffect(() => {
    let alive = true;
    api("/api/admin/plans")
      .then((p) => { if (alive) setPlans(p.plans || []); })
      .catch(() => { if (alive) setPlans([]); });
    return () => { alive = false; };
  }, []);
  return plans;
}

const planLine = (p) =>
  `${p.name_en} — $${p.usd_month}/month · ` +
  (p.all_styles ? "every trading style" : `${p.style_count} style`);

// Creating a customer IS the business: cash is collected in person and the
// operator makes the account by hand. This form is that moment -- identity,
// a first password to hand over, the plan that was paid for, and the day the
// paid period ends.
//
// The plan belongs HERE, in the same step. Until today nothing on any
// operator screen could put a customer on one: `subscriptions` was written
// only by the customer-app checkout, so every account this form made came out
// on no plan -- one style of alerts whatever was handed over, and $0 on the
// owner's own money card.
function NewCustomer({ onDone, onCancel, opener }) {
  const [form, setForm] = useState({ username: "", password: "", email: "",
                                     phone_number: "", expires_at: "", plan: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const panel = useOpenedPanel("input", opener);
  const plans = usePlans();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = useCallback(async () => {
    if (!form.username.trim()) {
      setError("Username is required."); return;
    }
    if (form.expires_at && !/^\d{4}-\d{2}-\d{2}$/.test(form.expires_at)) {
      setError("Access until must be YYYY-MM-DD."); return;
    }
    setBusy(true); setError(null);
    try {
      const r = await api("/api/admin/users", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: form.username.trim(), password: form.password,
                               email: form.email.trim(), phone_number: form.phone_number.trim() }),
      });
      if (!r.success && r.error) { setError(r.detail || r.error); setBusy(false); return; }
      const created = r.user;
      // The plan goes on BEFORE the access date, because activating one moves
      // the paid-through date to thirty days out and the date the operator
      // typed is the last word on it.
      if (form.plan && created?.id) {
        const rp = await api(`/api/admin/users/${created.id}/subscription`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan: form.plan }),
        });
        if (!rp || !rp.success) {
          setError("The account was created, but the plan was NOT applied: "
                   + ((rp && (rp.detail || rp.error)) || "no answer from the server")
                   + ". Set it from the customer row before they use it, or they "
                   + "will receive one style of alerts.");
          setBusy(false);
          return;
        }
      }
      if (form.expires_at && created?.id) {
        const rx = await api(`/api/admin/users/${created.id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ expires_at: form.expires_at }),
        });
        if (rx && rx.error) {
          // Stop here. This used to set the error and then call onDone in the
          // same React batch, which unmounted the form before the message
          // could paint -- so an operator who had just taken cash typed
          // 2026-02-30, saw nothing at all, and left a customer with NO
          // expiry: permanent free access, silently.
          setError("The account was created, but the access date was refused: "
                   + (rx.detail || rx.error)
                   + ". Set it from the customer row before they use it.");
          setBusy(false);
          return;
        }
      }
      setBusy(false);
      onDone(created);
    } catch (e) { setError(String(e.message || e)); setBusy(false); }
  }, [form, onDone]);

  return html`
    <section className="card" ref=${panel} tabIndex="-1">
      <h2>New customer</h2>
      ${/* A hint under every field, on the owner's instruction. The rule
             applied throughout: say what the field DOES and what goes wrong
             if it is wrong, never what it is called. Two paragraphs of prose
             used to sit under the whole form carrying this, which is the
             wrong place -- nobody reads a paragraph after they have already
             filled the box it was about. */""}
      <div className="form-grid">
        <${Field} label="Username"
          hint="What they type to sign in. They cannot change it later, so use something they will recognise on the phone with you.">
          <input value=${form.username} onInput=${set("username")} autoComplete="off" />
        <//>
        <${Field} label="First password"
          hint="Leave it empty and it becomes 123456. Either way the app FORCES them to choose their own the first time they sign in, so this is a handover code and never their real password.">
          <input value=${form.password} onInput=${set("password")} autoComplete="off"
                 placeholder="empty = 123456" />
        <//>
        <${Field} label="Email"
          hint="Optional, and only for you: nothing is sent to it yet. Fill it in and the account is findable by email when they call.">
          <input value=${form.email} onInput=${set("email")} />
        <//>
        <${Field} label="Phone"
          hint="Optional. The fastest way to find one customer among hundreds on the Customers screen — the search reads this field.">
          <input value=${form.phone_number} onInput=${set("phone_number")} />
        <//>
        <${Field} label="Plan"
          hint="What the alert router reads. On NO plan a customer receives one style whatever they paid you, and the money card counts them as $0. Choosing one also sets the paid period to 30 days unless you type a date below.">
          <select value=${form.plan} onChange=${set("plan")}
                  disabled=${plans === null}>
            <option value="">no plan — one style of alerts only</option>
            ${(plans || []).map((p) => html`
              <option key=${p.key} value=${p.key}>${planLine(p)}</option>`)}
          </select>
        <//>
        <${Field} label="Access until"
          hint="The LAST day they are covered, as YYYY-MM-DD. Leave it empty to let the plan decide. Extend it when they pay again — an empty date on a paid account is permanent free access.">
          <input value=${form.expires_at} onInput=${set("expires_at")}
                 placeholder="YYYY-MM-DD" />
        <//>
      </div>
      ${plans !== null && plans.length === 0 && html`
        <p className="warn">The plan list could not be read, so only “no plan” can be chosen
          here. Set the plan from the customer row once it comes back.</p>`}
      ${error && html`<p className="neg">${error}</p>`}
      <div className="row-actions">
        <button disabled=${busy} onClick=${create}>${busy ? "…" : "create"}</button>
        <button disabled=${busy} onClick=${onCancel}>cancel</button>
      </div>
    </section>`;
}

// Shown once, and it has to be SEEN once. Opened from a row that can be
// thousands of pixels down the list, so it takes the screen and the focus
// like every other panel here.
function IssuedPassword({ issued, onClose, opener }) {
  const panel = useOpenedPanel(null, opener);
  return html`
    <section className="card" ref=${panel} tabIndex="-1">
      <h2>Temporary password for ${issued.user}</h2>
      <p className="metric"><code>${issued.password}</code></p>
      <p className="muted">
        Shown once. Give it to them on a channel you already trust — the app
        forces them to choose their own the moment they sign in with it.
      </p>
      <button onClick=${onClose}>Done</button>
    </section>`;
}

function CustomersScreen({ users, onReload }) {
  const [busy, setBusy] = useState(null);
  const [issued, setIssued] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [adding, setAdding] = useState(false);
  const plans = usePlans();
  // Finding one person among many.
  //
  // At 200 customers this screen was 19,000 pixels of table and 1,202
  // buttons with no way to narrow it: the operator's most common task --
  // "open the account of the person on the phone" -- meant scrolling and
  // reading. The list this searches is the one already loaded, so it costs
  // no request and answers as fast as the operator types.
  const [query, setQuery] = useState("");
  // The control a panel was opened FROM, so closing gives the keyboard back
  // to it. Captured on the click itself: a window.confirm between the click
  // and the panel blurs the button, and document.activeElement then reads
  // BODY -- which is where the keyboard used to be dropped.
  const opener = useRef(null);
  const from = (act) => (event) => { opener.current = event.currentTarget; act(); };

  // The screen is called Customers: customers first, operators after, each
  // group alphabetical. Four operators used to bury the one paying customer.
  const ordered = users ? [...users].sort((a, b) =>
    (a.is_admin ? 1 : 0) - (b.is_admin ? 1 : 0) ||
    String(a.username).localeCompare(String(b.username))) : null;
  const needle = query.trim().toLowerCase();
  const shown = !ordered ? null : (!needle ? ordered : ordered.filter((u) =>
    [u.username, u.email, u.phone_number, u.full_name]
      .some((field) => String(field || "").toLowerCase().includes(needle))));

  const toggleActive = useCallback(async (u) => {
    const disabling = !!u.is_active;
    if (disabling && !window.confirm(
      `Disable ${u.username}? They are signed out and refused everywhere until re-enabled.`)) return;
    setBusy(u.id);
    try {
      const r = await api(`/api/admin/users/${u.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      if (r && r.error) window.alert(r.detail || r.error);
    } catch (e) { window.alert("The change did not save: " + (e.message || e)); }
    setBusy(null);
    onReload();
  }, [onReload]);

  const reset = useCallback(async (u) => {
    // A password reset is irreversible for the user in session terms, so it
    // asks first and shows the temporary password exactly once.
    if (!window.confirm(`Reset the password for ${u.username}? Their sessions end immediately.`)) return;
    setBusy(u.id);
    try {
      const r = await api(`/api/admin/users/${u.id}/reset-password`, { method: "POST" });
      setIssued(r.success ? { user: u.username, password: r.temporary_password } : null);
    } catch (e) { setIssued(null); }
    setBusy(null);
    onReload();
  }, [onReload]);

  // ONE ACCOUNT, ONE PLACE — and the operator holds the key that unsticks it.
  //
  // A second sign-in is refused while an account is in use, and it frees
  // itself after twenty minutes of silence. That covers a closed laptop; it
  // does not cover a customer standing in front of a screen who needs in NOW,
  // and the first thing they do is call. This is that call, answered in one
  // press and written to the audit trail server-side, because releasing
  // somebody's session is a security-relevant act.
  const releaseSession = useCallback(async (u) => {
    if (!window.confirm(
      `Free ${u.username}'s account for a new sign-in? Any device still using it will be signed out on its next request.`)) return;
    setBusy(u.id);
    try {
      await api(`/api/admin/users/${u.id}/release-session`, { method: "POST" });
    } catch (e) { /* the reload below shows the real state either way */ }
    setBusy(null);
    onReload();
  }, [onReload]);

  // Cash collected in person buys a period; this is where the period is
  // written down. The date is the LAST covered day; past it, sign-in and
  // every request refuse until it is extended. Audited server-side.
  const setExpiry = useCallback(async (u) => {
    const answer = window.prompt(
      `Access for ${u.username} until (YYYY-MM-DD) — empty removes the limit:`,
      u.expires_at || "");
    if (answer === null) return;
    const value = answer.trim();
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      window.alert("Use YYYY-MM-DD, e.g. 2026-09-30.");
      return;
    }
    setBusy(u.id);
    try {
      // api() resolves 400s as a body, not a throw: an operator who just
      // took cash must not be able to typo "2026-02-30", see silence, and
      // walk away believing access was extended.
      const r = await api(`/api/admin/users/${u.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expires_at: value }),
      });
      if (r && r.error) window.alert(r.detail || r.error);
    } catch (e) { window.alert("The change did not save: " + (e.message || e)); }
    setBusy(null);
    onReload();
  }, [onReload]);

  // Promote to operator, or take it back.
  //
  // The API has always accepted is_admin and the screen never offered it, so
  // the only way to make somebody an operator was to edit the database by
  // hand. It is also the most consequential thing anyone can do here: an
  // operator holds the kill switch, the customer list, the broadcast, and the
  // power to promote further operators. So the confirm names all four rather
  // than asking "are you sure", and the server records it in the audit log --
  // which it did not do until today either.
  const setRole = useCallback(async (u) => {
    const promoting = !u.is_admin;
    const question = promoting
      ? `Make ${u.username} an OPERATOR?\n\nThey get the kill switch, every `
        + `customer's details, the broadcast to all subscribers, and the `
        + `power to make further operators. This is recorded in the audit log.`
      : `Take operator away from ${u.username}?\n\nThey keep their account `
        + `and lose every operator control. This is recorded in the audit log.`;
    if (!window.confirm(question)) return;
    setBusy(u.id);
    try {
      const r = await api(`/api/admin/users/${u.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_admin: promoting }),
      });
      if (r && r.error) window.alert(r.detail || r.error);
    } catch (e) {
      window.alert("The role did not change: " + (e.message || e));
    }
    setBusy(null);
    onReload();
  }, [onReload]);

  // What was bought, and the way back from buying the wrong thing.
  //
  // An operator collects cash and makes the account by hand, and that path had
  // no plan control at all -- so the customer sat on no subscription, which
  // `_scope_to_style` reads as "one style", and no screen in the product could
  // change it. The prompt is the same idiom as `access` above; the confirm
  // names what the customer will actually experience, because "are you sure"
  // tells an operator nothing about the money they just took.
  const setPlan = useCallback(async (u) => {
    if (!plans || plans.length === 0) {
      window.alert("The plan list could not be read, so nothing here can be "
                   + "changed safely. Reload the screen and try again.");
      return;
    }
    const menu = plans.map((p) => `  ${p.key} — $${p.usd_month}/mo or `
      + `$${p.usd_year}/yr (${p.year_months_free} months free) · `
      + (p.all_styles ? "every trading style" : `${p.style_count} style`)).join("\n");
    const answer = window.prompt(
      `Plan for ${u.username}:\n\n${menu}\n\n`
      + `Type a plan key, or "none" to take the plan away.`,
      u.plan || "");
    if (answer === null) return;
    const want = answer.trim().toLowerCase();
    if (want === (u.plan || "")) return;
    const chosen = plans.find((p) => p.key === want);
    if (!chosen && want !== "none" && want !== "") {
      window.alert(`There is no plan called “${answer.trim()}”. `
                   + `The plans are: ${plans.map((p) => p.key).join(", ")}.`);
      return;
    }
    // The term, asked only when a plan is actually being sold. Defaulting to
    // monthly on anything unrecognised rather than refusing: the customer is
    // standing there with cash and a typo must not stop the sale.
    let term = "month";
    if (chosen) {
      const answered = window.prompt(
        `${chosen.name_en} for ${u.username}:\n\n`
        + `  month — $${chosen.usd_month}, 30 days\n`
        + `  year  — $${chosen.usd_year}, 365 days (saves $${chosen.year_saves})\n\n`
        + `Type "month" or "year".`, "month");
      if (answered === null) return;
      term = answered.trim().toLowerCase() === "year" ? "year" : "month";
    }

    const removing = !chosen;
    if (removing && !u.plan) {
      window.alert(`${u.username} is not on a plan.`);
      return;
    }
    const question = removing
      ? `Take the ${u.plan} plan off ${u.username}?\n\nThey keep their account `
        + `and their access date. Their alerts drop to the single style they `
        + `chose, the plan's service switches are cleared, and $${u.plan_usd || 0} `
        + `comes off the monthly total. This is recorded in the audit log.`
      : `Put ${u.username} on ${chosen.name_en} — `
        + `$${term === "year" ? chosen.usd_year : chosen.usd_month} for `
        + `${term === "year" ? "a year" : "a month"}?\n\n`
        + `They receive ${chosen.all_styles ? "every trading style's" : "one style's"} `
        + `signals, the plan's services are switched on, and their access runs at `
        + `least ${term === "year" ? 365 : 30} more days.`
        + `${term === "year" ? ` The money card counts this as $${Math.round(chosen.usd_year / 12)}/month, not $${chosen.usd_year}.` : ""}`
        + `${u.plan ? ` The ${u.plan} plan ends.` : ""} `
        + `This is recorded in the audit log.`;
    if (!window.confirm(question)) return;
    setBusy(u.id);
    try {
      const r = removing
        ? await api(`/api/admin/users/${u.id}/subscription`, { method: "DELETE" })
        : await api(`/api/admin/users/${u.id}/subscription`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ plan: chosen.key, term }),
          });
      // Money must never fail quietly: an operator who has taken cash and been
      // refused has to be told, or the customer is on nothing and nobody knows.
      if (!r || !r.success) {
        window.alert("The plan did not change: "
                     + ((r && (r.detail || r.error)) || "no answer from the server"));
      }
    } catch (e) {
      window.alert("The plan did not change: " + (e.message || e));
    }
    setBusy(null);
    onReload();
  }, [plans, onReload]);

  // The three states an operator acts on: lapsed, about to lapse, fine.
  const accessCell = (u) => {
    if (u.is_admin || !u.expires_at) return { text: "—", cls: "muted" };
    const today = new Date().toISOString().slice(0, 10);
    if (u.expires_at < today) return { text: `ended ${u.expires_at}`, cls: "neg" };
    const daysLeft = Math.round((Date.parse(u.expires_at) - Date.parse(today)) / 86400000);
    if (daysLeft <= 7) return { text: `${u.expires_at} · ${daysLeft}d left`, cls: "warn" };
    return { text: u.expires_at, cls: "" };
  };

  // The plan cell says what the ROUTER will do, not what was intended: the
  // server fills this from the same lookup the alert fan-out uses. "no plan"
  // is a warning, not a blank, because it means one style of alerts for
  // somebody who may well have paid for both.
  const planCell = (u) => {
    if (u.is_admin) return { text: "—", cls: "muted" };
    if (u.plan_unknown) return { text: "not read", cls: "warn" };
    if (!u.plan) return { text: "no plan · 1 style", cls: "warn" };
    return { text: `${u.plan}${u.plan_all_styles ? " · all styles" : " · 1 style"}`,
             cls: "pos" };
  };

  if (!users) return html`<p className="muted">Reading the customer list…</p>`;
  return html`
    <div className="stack">
      ${issued && html`
        <${IssuedPassword} issued=${issued} opener=${opener}
                          onClose=${() => setIssued(null)} />`}
      ${adding && html`
        <${NewCustomer} opener=${opener}
                        onDone=${() => { setAdding(false); onReload(); }}
                        onCancel=${() => setAdding(false)} />`}
      ${viewing && html`
        <${CustomerDetail} user=${viewing} opener=${opener}
                          onClose=${() => setViewing(null)} />`}
      ${editing && html`
        <${CustomerServices} user=${editing} opener=${opener}
                            onClose=${() => setEditing(null)} />`}
      <section className="card">
        <h2>Customers <small className="zone-note">times in ${OPERATOR_ZONE}</small>
          <span className="muted">${needle ? `${shown.length} of ${users.length}` : users.length}</span>
          <button className="head-action" onClick=${from(() => setAdding(true))}>+ new customer</button>
        </h2>
        <label className="customer-filter">
          <span className="muted">Find</span>
          <input type="search" value=${query} placeholder="username, email or phone"
                 onInput=${(e) => setQuery(e.target.value)} />
        </label>
        ${needle && shown.length === 0 && html`
          <p className="muted">No account matches “${query.trim()}”.</p>`}
        <div className="table-wrap">
          <table className="stacks">
            <thead><tr><th>user</th><th>contact</th><th>role</th><th>state</th>
                       <th>plan</th><th>access until</th><th>last seen</th><th></th></tr></thead>
            <tbody>
              ${shown.map((u) => {
                const access = accessCell(u);
                const planned = planCell(u);
                return html`
                <tr key=${u.id} className=${u.is_active ? "" : "is-disabled-row"}>
                  <td data-label="user">${u.username}</td>
                  <td data-label="contact">${u.email || u.phone_number || "—"}</td>
                  <td data-label="role">${u.is_admin ? "operator" : "customer"}</td>
                  <td data-label="state" className=${u.is_active ? "pos" : "neg"}>
                    ${u.is_active ? "active" : "disabled"}
                    ${u.must_change_password ? " · must change pw" : ""}
                  </td>
                  <td data-label="plan" className=${planned.cls}>${planned.text}</td>
                  <td data-label="access until" className=${access.cls}>${access.text}</td>
                  <td data-label="last seen">${fmtSeen(u.last_login)}</td>
                  <td className="row-actions">
                    <button onClick=${from(() => setViewing(u))}>view</button>
                    <button onClick=${from(() => setEditing(u))}>services</button>
                    ${!u.is_admin && html`
                      <button disabled=${busy === u.id} onClick=${() => setPlan(u)}>
                        ${u.plan ? "change plan" : "set plan"}
                      </button>`}
                    <button disabled=${busy === u.id} onClick=${() => setExpiry(u)}>access</button>
                    <button disabled=${busy === u.id} onClick=${() => toggleActive(u)}>
                      ${u.is_active ? "disable" : "enable"}
                    </button>
                    <button className=${u.is_admin ? "btn-warn" : ""}
                            disabled=${busy === u.id} onClick=${() => setRole(u)}>
                      ${u.is_admin ? "remove operator" : "make operator"}
                    </button>
                    ${!u.is_admin && html`
                      <button disabled=${busy === u.id}
                              onClick=${from(() => releaseSession(u))}>
                        free session
                      </button>`}
                    <button disabled=${busy === u.id} onClick=${from(() => reset(u))}>
                      ${busy === u.id ? "…" : "reset password"}
                    </button>
                  </td>
                </tr>`;})}
            </tbody>
          </table>
        </div>
      </section>
    </div>`;
}

// The caps the server enforces. Stated here too, so a message is refused
// while it can still be edited rather than after it has been typed.
const BROADCAST_TITLE_MAX = 80;
const BROADCAST_BODY_MAX = 500;

// The frame the engine owns and how often it wakes. Both were a deploy-time
// decision -- an env var and a constant -- so the owner could read them off his
// own dashboard and had no way to move either without editing a file and
// restarting the process that publishes.
//
// OPERATIONAL, not strategy. Which frame to watch and how often to look are
// his; the stop distances, targets and gates are not, and may only change on a
// measurement that clears the master law. This screen moves the first and has
// no control anywhere for the second, on purpose.
function DaemonSettings() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [problem, setProblem] = useState(null);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await api("/api/admin/daemon");
      if (!r || !r.daemon) { setProblem("The engine settings could not be read."); return; }
      setState(r);
      setForm({ timeframe: r.daemon.timeframe,
                interval_s: String(r.daemon.interval_s) });
    } catch (e) {
      setProblem("Could not read the engine settings — the request did not reach the server.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (event) => {
    event.preventDefault();
    setBusy(true); setNote(null); setProblem(null);
    try {
      const r = await api("/api/admin/daemon", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timeframe: form.timeframe,
                               interval_s: Number(form.interval_s) }),
      });
      if (!r || !r.daemon) { setProblem((r && r.detail) || "That was refused."); }
      else { setState({ ...state, daemon: r.daemon });
             setNote(r.note || "Saved."); }
    } catch (e) { setProblem("The change did not reach the server."); }
    setBusy(false);
  }, [form, state]);

  if (problem && !state) {
    return html`<section className="card"><h2>The engine</h2>
      <p className="note-bad" role="alert">${problem}</p>
      <button onClick=${load}>Try again</button></section>`;
  }
  if (!state || !form) {
    return html`<section className="card"><h2>The engine</h2>
      <p className="muted">Reading the engine settings…</p></section>`;
  }

  const bounds = state.interval_bounds || [];
  return html`
    <section className="card">
      <h2>The engine</h2>
      <p className="op-hint">
        Which frame the daemon watches and how often it looks. These move
        without a restart. What the engine DOES on that frame — stops, targets,
        gates — is not on this screen and is not a setting: it changes only on
        a measurement.
      </p>
      ${problem && html`<p className="note-bad" role="alert">${problem}</p>`}
      <form className="admin-form" onSubmit=${save}>
        <${Field} label="Daemon frame"
          hint="The frame alerts and the published record are measured on. Changing it changes which market this product is watching, so the record before and after describes two different things.">
          <select value=${form.timeframe}
                  onChange=${(e) => setForm({ ...form, timeframe: e.target.value })}>
            ${(state.valid_timeframes || []).map((tf) => html`
              <option key=${tf} value=${tf}>${tf}</option>`)}
          </select>
        <//>
        <${Field} label="Loop interval (seconds)"
          hint=${`How often the engine wakes, fetches and decides. Between ${bounds[0]} and ${bounds[1]}s — below ${bounds[0]}s the loop re-enters before its own market fetch has returned, which stacks requests against a source that rate-limits us and makes the product quieter, not faster.`}>
          <input type="number" step="10" value=${form.interval_s}
                 onInput=${(e) => setForm({ ...form, interval_s: e.target.value })} />
        <//>
        <div className="row-actions">
          <button type="submit" disabled=${busy}>${busy ? "Saving…" : "Save"}</button>
          <small className="muted">
            Default ${state.daemon.defaults.timeframe} ·
            ${state.daemon.defaults.interval_s}s
          </small>
        </div>
      </form>
      ${note && html`<p className="note-ok" role="status">${note}</p>`}
    </section>`;
}

// The two numbers that decide when the product goes silent. They lived in
// src/risk/limits.py as constants, so the owner could watch the engine halt on
// a rule he had no way to move without editing code and restarting.
//
// It sits in Actions beside the kill switch, because those three controls are
// one thought: how much do I let this thing lose before I stop it, and the
// button for stopping it now.
function RiskLimits() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [problem, setProblem] = useState(null);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await api("/api/admin/risk-limits");
      if (!r || !r.limits) { setProblem("The limits could not be read."); return; }
      setState(r);
      setForm({ daily_loss_r: String(r.limits.daily_loss_r),
                consecutive_losses: String(r.limits.consecutive_losses) });
    } catch (e) {
      setProblem("Could not read the risk limits — the request did not reach the server.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (event) => {
    event.preventDefault();
    setBusy(true); setNote(null); setProblem(null);
    try {
      const r = await api("/api/admin/risk-limits", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          daily_loss_r: Number(form.daily_loss_r),
          consecutive_losses: Number(form.consecutive_losses),
        }),
      });
      // The server refuses out-of-bounds values with the bounds named rather
      // than clamping them, so its sentence is the one worth showing.
      if (!r || !r.limits) { setProblem((r && r.detail) || "That was refused."); }
      else { setState({ ...state, limits: r.limits }); setNote("Saved. It applies to the next decision."); }
    } catch (e) { setProblem("The change did not reach the server."); }
    setBusy(false);
  }, [form, state]);

  if (problem && !state) {
    return html`<section className="card"><h2>Risk limits</h2>
      <p className="note-bad" role="alert">${problem}</p>
      <button onClick=${load}>Try again</button></section>`;
  }
  if (!state || !form) {
    return html`<section className="card"><h2>Risk limits</h2>
      <p className="muted">Reading the limits…</p></section>`;
  }

  const b = state.bounds || {};
  return html`
    <section className="card">
      <h2>Risk limits</h2>
      <p className="op-hint">
        These two stop publishing on their own. The kill switch above is the
        manual version of the same decision${state.limits.overridden
          ? "" : " — both are at their defaults right now"}.
      </p>
      ${problem && html`<p className="note-bad" role="alert">${problem}</p>`}
      <form className="admin-form" onSubmit=${save}>
        <${Field} label="Daily loss limit (R)"
          hint=${`Publishing stops for the day once the day's resolved calls add up to this loss. Between ${(b.daily_loss_r || [])[0]}R and ${(b.daily_loss_r || [])[1]}R — lower is quieter, higher lets a bad day run longer.`}>
          <input type="number" step="0.5" value=${form.daily_loss_r}
                 onInput=${(e) => setForm({ ...form, daily_loss_r: e.target.value })} />
        <//>
        <${Field} label="Consecutive losses"
          hint=${`This many losing calls in a row on one style stops that style for the session — it re-arms next session on its own. Between ${(b.consecutive_losses || [])[0]} and ${(b.consecutive_losses || [])[1]}.`}>
          <input type="number" step="1" value=${form.consecutive_losses}
                 onInput=${(e) => setForm({ ...form, consecutive_losses: e.target.value })} />
        <//>
        <div className="row-actions">
          <button type="submit" disabled=${busy}>${busy ? "Saving…" : "Save limits"}</button>
          <small className="muted">
            Default ${state.limits.defaults.daily_loss_r}R ·
            ${state.limits.defaults.consecutive_losses} in a row
          </small>
        </div>
      </form>
      ${note && html`<p className="note-ok" role="status">${note}</p>`}
    </section>`;
}

function ControlsScreen({ health, onReload }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [formError, setFormError] = useState(null);
  const [msg, setMsg] = useState({ title: "", body: "" });

  // THREE states, not two.
  //
  // `health` is null when the payload could not be read at all, and
  // health.risk is {unavailable} when that one block failed. Both used to
  // collapse into `undefined` and then into `halted = false`, which this
  // screen rendered as a green "publishing allowed" light with a live Halt
  // button under it. With /api/admin/health returning 500 the operator was
  // shown a confident all-clear about a system the console could not see,
  // stamped "updated 0s ago". Not knowing is now its own state, and a
  // control we cannot describe is a control we do not offer.
  const unreadable = !health;
  const riskUnknown = unreadable || missing(health.risk);
  const halted = riskUnknown ? null : !!health.risk.kill_switch;
  const subscribers = (!unreadable && !missing(health.alerts))
    ? (health.alerts.subscribers ?? null) : null;

  const toggleKill = useCallback(async () => {
    if (riskUnknown) return;
    const next = !halted;
    if (next && !window.confirm("Halt ALL publishing? No alert or trade can open until you re-arm.")) return;
    setBusy(true);
    try {
      const r = await api("/api/admin/kill-switch", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ on: next }),
      });
      setNote(r && r.success
        ? (next ? "Publishing halted." : "Publishing re-armed.")
        : "The kill switch did not change — the server refused it.");
    } catch (e) { setNote("Could not change the kill switch."); }
    setBusy(false);
    onReload();
  }, [halted, riskUnknown, onReload]);

  const broadcast = useCallback(async (event) => {
    event.preventDefault();
    // A button that fires nothing and says nothing is a broken button. Both
    // fields empty used to produce exactly that: zero requests, no message,
    // no clue which field was wrong.
    const title = msg.title.trim();
    const body = msg.body.trim();
    if (!title || !body) {
      setFormError(!title && !body
        ? "A title and a message are both required."
        : !title ? "A title is required." : "A message is required.");
      return;
    }
    if (title.length > BROADCAST_TITLE_MAX || body.length > BROADCAST_BODY_MAX) {
      setFormError(`The title is capped at ${BROADCAST_TITLE_MAX} characters `
        + `and the message at ${BROADCAST_BODY_MAX}. Nothing was sent.`);
      return;
    }
    setFormError(null);
    if (!window.confirm("Send this to every subscriber?")) return;
    setBusy(true);
    try {
      const r = await api("/api/admin/broadcast", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, body }),
      });
      if (r && r.success) {
        setNote(r.recipients === 0
          ? "Nobody has alerts enabled — nothing was sent."
          : `Queued to ${r.recipients} user(s).`);
        // Cleared ONLY here. A refusal used to wipe both fields
        // unconditionally, so an announcement that tripped the 500-character
        // cap was deleted by the act of being refused -- the operator lost
        // the whole message and was told to shorten something that no longer
        // existed.
        setMsg({ title: "", body: "" });
      } else {
        setFormError((r && (r.detail || r.error)) || "Broadcast refused.");
      }
    } catch (e) { setFormError("The broadcast did not reach the server."); }
    setBusy(false);
  }, [msg]);

  return html`
    <div className="stack">
      ${unreadable && html`
        <p className="note-bad" role="status">
          The system state could not be read, so neither control below can be
          offered: this screen does not know whether publishing is running.
        </p>`}
      <section className="card">
        <h2>Kill switch</h2>
        <div className="metric">
          ${riskUnknown
            ? html`<${NotReported} block=${health && health.risk} />`
            : html`
              <${Light} ok=${!halted} warn=${false}>
                ${halted ? "PUBLISHING HALTED" : "publishing allowed"}
              <//>`}
        </div>
        <p className="muted">
          Halts both publication paths — alerts and opening a position — before
          any setup check, and a user's override does not bypass it.
        </p>
        ${/* This rendered with no class at all -- byte-identical to
             "refresh" and "view" -- while eight routine service toggles were
             filled red. The control that stops publishing for every customer
             was the calmest button on the page. */""}
        <button className=${halted ? "btn-ok" : "btn-critical"}
                disabled=${busy || riskUnknown} onClick=${toggleKill}>
          ${riskUnknown ? "Halt publishing"
            : halted ? "Re-arm publishing" : "Halt publishing"}
        </button>
        ${riskUnknown && html`
          <p className="muted">
            Disabled: flipping a switch whose current position is unknown is
            how publishing gets halted twice and re-armed by accident.
          </p>`}
      </section>

      <${RiskLimits} />

      <${DaemonSettings} />

      <section className="card">
        <h2>Broadcast</h2>
        ${formError && html`<p className="note-bad" role="alert">${formError}</p>`}
        ${/* These two boxes were bare placeholders with the warning in a
             paragraph BELOW the send button -- the arrangement U5 was written
             against. A broadcast cannot be recalled, so the sentence that says
             so has to arrive before the typing, not after it. */""}
        <p className="op-hint">
          ${subscribers == null
            ? "The subscriber count is not reported right now, so this send "
              + "cannot tell you how many phones it will reach."
            : `Goes to all ${subscribers} subscriber(s) at once.`}
          ${" "}A sent broadcast cannot be edited or recalled.
        </p>
        <form className="admin-form" onSubmit=${broadcast}>
          <${Field} label="Title"
            hint="The bold line on the phone's lock screen, and often the only part read. Say the thing itself — “Rooms are back” beats “Update”.">
            <input placeholder="Title" value=${msg.title}
                   maxLength=${BROADCAST_TITLE_MAX}
                   onInput=${(e) => setMsg({ ...msg, title: e.target.value })} />
          <//>
          <${Field} label="Message"
            hint=${`Two lines at most on a phone; the rest is cut. This is an announcement to every customer at once, so it must not read as a trading call — a setup arriving has its own badge and this does not.`}>
            <textarea placeholder="Message" rows="3" value=${msg.body}
                      maxLength=${BROADCAST_BODY_MAX}
                      onInput=${(e) => setMsg({ ...msg, body: e.target.value })}></textarea>
          <//>
          <div className="row-actions">
            <button type="submit" disabled=${busy || unreadable}>Send to subscribers</button>
            <small className="muted">
              ${msg.body.length}/${BROADCAST_BODY_MAX} characters
            </small>
          </div>
        </form>
      </section>

      ${note && html`<p className="note-ok" role="status">${note}</p>`}
    </div>`;
}

// The public website, owner-controlled: media slots the landing renders
// only when filled, and the payments customers are still waiting on.
function WebsiteScreen() {
  const [slots, setSlots] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  // A refusal is not a note. They rendered as the same grey line at the foot
  // of the page -- if they rendered at all.
  const [problem, setProblem] = useState(null);

  const [subs, setSubs] = useState(null);
  const [copy, setCopy] = useState(null);
  const load = useCallback(async () => {
    try {
      setSlots((await api("/api/admin/site-assets")).slots);
      setSubs((await api("/api/admin/subscriptions/pending")).pending);
      setCopy((await api("/api/admin/site-copy")).copy);
    } catch (e) { setProblem("Could not read the website state."); }
  }, []);

  const saveCopy = useCallback(async () => {
    setBusy("copy");
    setProblem(null);
    try {
      const r = await api("/api/admin/site-copy", {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(copy),
      });
      if (r && r.success) {
        setCopy(r.copy);
        setNote("Contact facts are live on the site.");
      } else {
        // A refusal used to land nowhere at all: `if (r.success)` with no
        // else, so a link the server rejected left the field looking saved.
        // The server refuses any scheme that is not http(s) -- that check is
        // what stops a javascript: URL reaching an href on the public
        // landing page -- and the operator has to be told which field.
        setNote(null);
        setProblem(r && r.error === "bad_url"
          ? `Nothing was saved — ${r.detail}. Fix the field and save again.`
          : `Nothing was saved: ${(r && (r.detail || r.error)) || "the server refused it"}.`);
      }
    } catch (e) { setProblem("Could not save the contact facts."); }
    setBusy(null);
  }, [copy]);
  useEffect(() => { load(); }, [load]);

  const upload = useCallback(async (slot, file) => {
    if (!file) return;
    setBusy(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api(`/api/admin/site-assets/${slot}`, {
        method: "POST", body: fd,
      });
      if (r && r.success) { setProblem(null); setNote(`${slot} is live on the landing.`); load(); }
      else setProblem(r && r.error === "too_large"
        ? `Too large — the limit for ${slot} is ${r.max_mb} MB.`
        : r && r.error === "bad_type"
          ? `Wrong type — ${slot} takes: ${(r.allowed || []).join(", ")}.`
          : "The upload did not take.");
    } catch (e) { setProblem("The upload did not take."); }
    setBusy(null);
  }, [load]);

  const remove = useCallback(async (slot) => {
    if (!window.confirm(`Remove ${slot} from the landing?`)) return;
    setBusy(slot);
    try { await api(`/api/admin/site-assets/${slot}`, { method: "DELETE" }); load(); }
    catch (e) { setProblem("Could not remove it."); }
    setBusy(null);
  }, [load]);

  const markPaid = useCallback(async (s) => {
    if (!window.confirm(
      `Confirm: $${s.amount_usd} received from ${s.username} for ${s.plan}? ` +
      "This activates the plan's services immediately.")) return;
    setBusy(`sub-${s.id}`);
    try {
      const r = await api(`/api/admin/subscriptions/${s.id}/mark-paid`, {
        method: "POST" });
      if (r && r.success) { setProblem(null); setNote(`${s.username} is now on ${s.plan}.`); }
      else setProblem(`Could not activate (${(r && (r.detail || r.error)) || "unknown"}).`);
      load();
    } catch (e) { setProblem("Could not activate."); }
    setBusy(null);
  }, [load]);


  const SLOT_LABELS = {
    promo_video: ["Promo video", "Plays at the top of the “See it work” section instead of the animation."],
    shot_1: ["Product picture 1", "Shown with picture 2 in a “From the product” strip — only when uploaded."],
    shot_2: ["Product picture 2", "Shown with picture 1 — the strip stays hidden until at least one exists."],
  };

  return html`
    <div className="stack">
      <section className="card">
        <h2>Landing media</h2>
        <p className="muted">
          What you upload here is live on the public site immediately.
          Empty slots simply do not render — the page never shows a hole.
        </p>
        ${!slots ? html`<p className="muted">…</p>` : html`
          <ul className="feature-list">
            ${slots.map((s) => html`
              <li key=${s.slot}>
                <div className="feature-head">
                  <${Light} ok=${!!s.url} warn=${false}>
                    ${(SLOT_LABELS[s.slot] || [s.slot])[0]}
                  <//>
                  <span>
                    ${s.url && html`
                      <a href=${s.url} target="_blank" rel="noopener"
                         style=${{ marginInlineEnd: "10px" }}>view</a>`}
                    ${/* The input is visually hidden, NOT `hidden`.
                         `hidden` is display:none, which takes an element out
                         of the tab order entirely -- and the label around it
                         is not focusable either, so a keyboard-only operator
                         could reach "remove" on all three landing slots and
                         could not reach "upload" on any of them: they could
                         empty the public site and not put it back. The input
                         is now focusable and opens the picker on Enter or
                         Space; :focus-within rings the label. */""}
                    <label className="admin-upload">
                      ${busy === s.slot ? "…" : s.url ? "replace" : "upload"}
                      <input type="file" className="visually-hidden"
                             aria-label=${`${s.url ? "Replace" : "Upload"} `
                               + `${(SLOT_LABELS[s.slot] || [s.slot])[0]}`}
                             accept=${s.exts.map((x) => "." + x).join(",")}
                             onChange=${(e) => { upload(s.slot, e.target.files[0]); e.target.value = ""; }} />
                    </label>
                    ${s.url && html`
                      <button className="btn-danger" disabled=${busy === s.slot}
                              onClick=${() => remove(s.slot)}>remove</button>`}
                  </span>
                </div>
                <p className="muted">
                  ${(SLOT_LABELS[s.slot] || ["", ""])[1]}
                  ${" "}(${s.exts.join("/")}, up to ${s.max_mb} MB${s.size_kb ? ` — current: ${s.size_kb} KB` : ""})
                </p>
              </li>`)}
          </ul>`}
      </section>

      <section className="card">
        <h2>Payments awaiting confirmation</h2>
        <p className="muted">
          Customers who chose a plan at signup. The moment their Whish
          transfer lands, mark it paid — the plan's services switch on
          immediately.
        </p>
        ${!subs ? html`<p className="muted">…</p>`
          : subs.length === 0
            ? html`<p className="metric ok">No one is waiting on a payment.</p>`
            : html`
              <table className="admin-table stacks">
                <thead><tr>
                  <th>User</th><th>Name</th><th>Phone</th><th>Plan</th>
                  <th>Amount</th><th></th>
                </tr></thead>
                <tbody>
                  ${subs.map((s) => html`
                    <tr key=${s.id}>
                      <td>${s.username}</td>
                      <td>${s.full_name || "—"}</td>
                      <td>${s.phone_number || "—"}</td>
                      <td>${s.plan}</td>
                      <td>$${s.amount_usd}</td>
                      <td>
                        <button disabled=${busy === `sub-${s.id}`}
                                onClick=${() => markPaid(s)}>
                          ${busy === `sub-${s.id}` ? "…" : "mark paid"}
                        </button>
                      </td>
                    </tr>`)}
                </tbody>
              </table>`}
      </section>

      <section className="card">
        <h2>Contact & socials</h2>
        <p className="muted">
          These render on the public site. Leave a field empty to HIDE
          that channel — the page never shows a dead link.
        </p>
        ${!copy ? html`<p className="muted">…</p>` : html`
          <div className="admin-form">
            ${[["phone", "Phone (display format)"],
               ["whatsapp", "WhatsApp number (digits only)"],
               ["email", "Contact email"],
               ["instagram", "Instagram URL"],
               ["x", "X URL"],
               ["youtube", "YouTube URL"]].map(([k, label]) => html`
              <label key=${k} style=${{ display: "block", marginBlock: "8px" }}>
                <span className="muted" style=${{ fontSize: "12px" }}>${label}</span>
                <input style=${{ width: "100%" }} value=${copy[k] || ""}
                       onInput=${(e) => setCopy({ ...copy, [k]: e.target.value })} />
              </label>`)}
            <button disabled=${busy === "copy"} onClick=${saveCopy}>
              ${busy === "copy" ? "…" : "Save to the site"}
            </button>
          </div>`}
      </section>

      ${problem && html`<p className="note-bad" role="alert">${problem}</p>`}
      ${note && html`<p className="note-ok" role="status">${note}</p>`}
    </div>`;
}

// --------------------------------------------------------------------- shell

// --- Telegram -------------------------------------------------------------
//
// The operator's own rooms on a channel they run. Nothing here reaches a
// customer: the app's own notifications are unaffected by every switch on
// this screen.
function TelegramScreen() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [form, setForm] = useState({ label: "", bot_token: "", chat_id: "", style_key: "*" });

  const load = useCallback(async () => {
    try { setState(await api("/api/admin/telegram")); }
    catch (e) {
      // The reason, not just the failure. `wrong-port` is the one an operator
      // actually hits, and telling them "could not read the settings" when
      // the truth is "you are on the customer app's address" is how an hour
      // gets lost -- it already did, once, on this app.
      setState({ success: false, why: e.message });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const say = (text, bad) => { setNote({ text, bad: !!bad }); setTimeout(() => setNote(null), 6000); };

  const toggleChannel = async () => {
    setBusy(true);
    try {
      const r = await api("/api/admin/telegram/running", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ running: !state.running }),
      });
      if (r.success) await load();
    } finally { setBusy(false); }
  };

  const add = async () => {
    setBusy(true);
    try {
      const r = await api("/api/admin/telegram/destinations", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.success) {
        setForm({ label: "", bot_token: "", chat_id: "", style_key: "*" });
        say("Room added. Send a test before you rely on it.");
        await load();
      } else say(TG_ERRORS[r.error] || r.error || "That did not save.", true);
    } finally { setBusy(false); }
  };

  const test = async (id) => {
    setBusy(true);
    try {
      const r = await api(`/api/admin/telegram/destinations/${id}/test`, { method: "POST" });
      say(r.ok ? "Test message delivered." : `Telegram refused it: ${r.error}`, !r.ok);
      await load();
    } finally { setBusy(false); }
  };

  const patch = async (id, changes) => {
    setBusy(true);
    try {
      const r = await api(`/api/admin/telegram/destinations/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(changes),
      });
      if (!r.success) say(TG_ERRORS[r.error] || r.error, true);
      await load();
    } finally { setBusy(false); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Remove "${label}"? Calls stop reaching this room immediately.`)) return;
    setBusy(true);
    try { await api(`/api/admin/telegram/destinations/${id}`, { method: "DELETE" }); await load(); }
    finally { setBusy(false); }
  };

  if (!state) return html`<p className="muted">Loading…</p>`;
  if (state.success === false) {
    return html`
      <section className="card">
        <h2>Telegram</h2>
        <p className="note-bad">
          ${state.why === "wrong-port"
            ? "This is the customer app's address. The operator app runs on its own port."
            : state.why === "signed-out"
              ? "Your session ended. Sign in again to reach these settings."
              : "Could not read the Telegram settings. The server did not answer — nothing here is current, and nothing has been changed."}
        </p>
        <p className="op-hint">
          Rooms already configured keep working while this screen cannot be
          read: the channel is stored server-side, not here.
        </p>
        <button onClick=${load}>Try again</button>
      </section>`;
  }

  return html`
    <div className="grid">
      <section className="card">
        <h2>The channel <span className="muted">— published calls, to rooms you run</span></h2>
        <p className="op-lede">
          The engine sends these, not a person. Every message goes out the moment
          a call is sealed into the public record, and carries that call's record
          link — so anyone in the room can check that it was written down before
          the outcome was known. Nothing here touches what your customers receive
          in the app.
        </p>
        <div className="op-switch">
          <button className=${state.running ? "btn-danger" : ""}
                  disabled=${busy} onClick=${toggleChannel}>
            ${state.running ? "Stop broadcasting" : "Start broadcasting"}
          </button>
          <span className=${state.running ? "note-ok" : "muted"}>
            ${state.running
              ? "Running. The next published call goes out."
              : "Stopped. Rooms stay configured; nothing is being sent."}
          </span>
        </div>
        <small className="op-hint">
          This is the master switch. It stops every room at once without changing
          a single one of them, and it is read at the moment of sending — stop it
          and the very next call stays in.
        </small>
        ${note && html`<p className=${note.bad ? "note-bad" : "note-ok"}>${note.text}</p>`}
      </section>

      <section className="card">
        <h2>Add a room</h2>
        <div className="form-grid">
          <${Field} label="Name"
            hint="Yours only — it never leaves this screen. Name it after the room so a failure tells you which one broke.">
            <input value=${form.label} disabled=${busy}
                   onInput=${(e) => setForm({ ...form, label: e.target.value })} />
          <//>
          <${Field} label="Bot token"
            hint="From @BotFather in Telegram, shaped 8123456789:AA… . Anyone holding it can post as your bot, so it is stored and never shown again — to change it, type a new one.">
            <input value=${form.bot_token} disabled=${busy} autoComplete="off"
                   placeholder="8123456789:AA…"
                   onInput=${(e) => setForm({ ...form, bot_token: e.target.value })} />
          <//>
          <${Field} label="Chat ID"
            hint="The group's id, usually negative for a group (-1001234567890). Add your bot to the group first and make it an admin, or Telegram refuses the message.">
            <input value=${form.chat_id} disabled=${busy}
                   placeholder="-1001234567890"
                   onInput=${(e) => setForm({ ...form, chat_id: e.target.value })} />
          <//>
          <${Field} label="Profile"
            hint="Which style's calls this room gets. A scalping room does not want a 4h swing call at 3am. Choose “every style” for one room that takes all of them.">
            <select value=${form.style_key} disabled=${busy}
                    onChange=${(e) => setForm({ ...form, style_key: e.target.value })}>
              <option value=${state.all_styles_key}>every style</option>
              ${(state.styles || []).map((s) => html`
                <option key=${s.key} value=${s.key}>
                  ${s.key} · ${s.timeframe} · ${s.reward_risk}:1
                </option>`)}
            </select>
          <//>
        </div>
        <button disabled=${busy || !form.label || !form.bot_token || !form.chat_id}
                onClick=${add}>Add room</button>
      </section>

      <section className="card">
        <h2>Rooms <span className="muted">— ${(state.destinations || []).length}</span></h2>
        ${!(state.destinations || []).length && html`
          <p className="muted">No rooms yet. Add one above, then send it a test.</p>`}
        <ul className="op-list">
          ${(state.destinations || []).map((d) => html`
            <li key=${d.id} className=${d.enabled ? "" : "is-off"}>
              <div className="op-list-main">
                <b>${d.label}</b>
                <span className="muted">
                  ${d.style_key === "*" ? "every style" : d.style_key}
                  · chat ${d.chat_id} · bot ${d.bot_token_masked}
                </span>
                <span className=${d.last_error ? "note-bad" : "muted"}>
                  ${d.last_error
                    ? `last failure: ${d.last_error}`
                    : d.last_ok_at
                      ? `${d.sent_count} sent · last ok ${new Date(d.last_ok_at * 1000).toLocaleString()}`
                      : "nothing sent yet — send a test"}
                </span>
              </div>
              <div className="op-list-actions">
                <button disabled=${busy} onClick=${() => test(d.id)}>Test</button>
                <button disabled=${busy}
                        onClick=${() => patch(d.id, { enabled: !d.enabled })}>
                  ${d.enabled ? "Pause" : "Resume"}
                </button>
                <button className="btn-danger" disabled=${busy}
                        onClick=${() => remove(d.id, d.label)}>Remove</button>
              </div>
            </li>`)}
        </ul>
        <small className="op-hint">
          Pause stops one room and keeps its settings. Remove deletes it. Neither
          affects the app: your customers' notifications are a separate channel.
        </small>
      </section>
    </div>`;
}

const TG_ERRORS = {
  label_required: "Give the room a name.",
  bad_token: "That token is not shaped like a Telegram bot token (digits, a colon, then a long secret).",
  chat_id_required: "The chat id is missing.",
  unknown_style: "That profile does not exist.",
  already_registered: "This chat is already registered for that profile.",
  no_such_destination: "That room is gone.",
};

// --- In-app rooms ---------------------------------------------------------
function RoomsScreen() {
  const [rooms, setRooms] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const [loadFailed, setLoadFailed] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api("/api/admin/groups");
      setRooms(r.groups || []);
      setLoadFailed(null);
    } catch (e) {
      // NOT setRooms([]). An empty list renders "No rooms yet", which tells
      // the operator their rooms are gone when the truth is that this screen
      // could not read them.
      setLoadFailed(e.message || "unreachable");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRoom = async (id) => {
    setOpenId(id); setDetail(null);
    try { setDetail(await api(`/api/admin/groups/${id}/members`)); }
    catch (e) { setDetail({ members: [], candidates: [] }); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const r = await api("/api/admin/groups", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.success) { setForm({ name: "", description: "" }); await load(); openRoom(r.group.id); }
      else setNote(r.error);
    } finally { setBusy(false); }
  };

  const act = async (path, options) => {
    setBusy(true);
    try { await api(path, options); if (openId) await openRoom(openId); await load(); }
    finally { setBusy(false); }
  };

  return html`
    <div className="grid">
      <section className="card">
        <h2>Open a room</h2>
        <p className="op-lede">
          Rooms inside the app. You open them and you choose who is in them —
          a customer cannot open one or invite anyone, which is what keeps this
          from becoming a place strangers message strangers.
        </p>
        <div className="form-grid">
          <${Field} label="Name"
            hint="What the customer sees at the top of the room. Name it for who is in it: “Scalping desk”, not “Group 2”.">
            <input value=${form.name} disabled=${busy}
                   onInput=${(e) => setForm({ ...form, name: e.target.value })} />
          <//>
          <${Field} label="Description"
            hint="One line under the name, shown on the invitation. This is what the customer reads when deciding whether to join.">
            <input value=${form.description} disabled=${busy}
                   onInput=${(e) => setForm({ ...form, description: e.target.value })} />
          <//>
        </div>
        <button disabled=${busy || !form.name.trim()} onClick=${create}>Open room</button>
        ${note && html`<p className="note-bad">${note}</p>`}
      </section>

      <section className="card">
        <h2>Rooms <span className="muted">— ${(rooms || []).length}</span></h2>
        ${loadFailed && html`
          <p className="note-bad">
            ${loadFailed === "wrong-port"
              ? "This is the customer app's address. The operator app runs on its own port."
              : "Could not read the rooms. They still exist — this screen could not reach the server."}
            ${" "}<button onClick=${load}>Try again</button>
          </p>`}
        ${!loadFailed && rooms === null && html`<p className="muted">Loading…</p>`}
        ${!loadFailed && rooms !== null && !rooms.length
          && html`<p className="muted">No rooms yet.</p>`}
        <ul className="op-list">
          ${(rooms || []).map((r) => html`
            <li key=${r.id} className=${openId === r.id ? "is-open" : ""}>
              <div className="op-list-main">
                <b>${r.name}</b>
                <span className="muted">
                  ${r.joined} joined · ${r.invited} invited · ${r.messages} messages
                  ${r.archived_at ? " · closed" : ""}
                </span>
              </div>
              <div className="op-list-actions">
                <button disabled=${busy} onClick=${() => openRoom(r.id)}>Members</button>
                ${!r.archived_at && html`
                  <button className="btn-danger" disabled=${busy}
                          onClick=${() => {
                            if (window.confirm(`Close "${r.name}"? Nobody can post; everything said stays readable.`))
                              act(`/api/admin/groups/${r.id}/archive`, { method: "POST" });
                          }}>Close</button>`}
              </div>
            </li>`)}
        </ul>
      </section>

      ${openId && html`
        <section className="card">
          <h2>Members</h2>
          ${detail === null && html`<p className="muted">Loading…</p>`}
          ${detail && html`
            <ul className="op-list">
              ${detail.members.map((m) => html`
                <li key=${m.user_id}>
                  <div className="op-list-main">
                    <b>${m.username}${m.is_owner ? " (you)" : ""}</b>
                    <span className="muted">
                      ${m.state}${m.can_post ? "" : " · silenced"}
                    </span>
                  </div>
                  ${!m.is_owner && m.state === "joined" && html`
                    <div className="op-list-actions">
                      <button disabled=${busy}
                              onClick=${() => act(`/api/admin/groups/${openId}/members/${m.user_id}/posting`, {
                                method: "POST", headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ allowed: !m.can_post }),
                              })}>
                        ${m.can_post ? "Silence" : "Allow writing"}
                      </button>
                      <button className="btn-danger" disabled=${busy}
                              onClick=${() => {
                                if (window.confirm(`Remove ${m.username} from this room?`))
                                  act(`/api/admin/groups/${openId}/members/${m.user_id}`, { method: "DELETE" });
                              }}>Remove</button>
                    </div>`}
                </li>`)}
            </ul>
            <small className="op-hint">
              Silencing is not removal: a silenced member keeps reading the room
              and keeps receiving what is said in it. Removing takes the room out
              of their app entirely; inviting them again brings it back.
            </small>

            <h3>Invite</h3>
            ${!detail.candidates.length && html`
              <p className="muted">Every active customer is already in this room.</p>`}
            <ul className="op-list">
              ${detail.candidates.map((c) => html`
                <li key=${c.id}>
                  <div className="op-list-main">
                    <b>${c.username}</b><span className="muted">${c.email || ""}</span>
                  </div>
                  <div className="op-list-actions">
                    <button disabled=${busy}
                            onClick=${() => act(`/api/admin/groups/${openId}/invite`, {
                              method: "POST", headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ user_ids: [c.id] }),
                            })}>Invite</button>
                  </div>
                </li>`)}
            </ul>
            <small className="op-hint">
              An invitation is not access: until they accept, they cannot read a
              word of what was said in the room.
            </small>`}
        </section>`}
    </div>`;
}

// The operator app's own address, built from where this page actually is.
// Hardcoding a port would be wrong for every deployment but the developer's;
// this follows the running configuration, and falls back to the documented
// default only when the page is being served from somewhere unexpected.
// Where the two apps live, DERIVED instead of hardcoded. The old constants
// assumed the dev two-port topology (host:5000 / host:5001) and were simply
// wrong behind the production subdomains (mithqal-v2.varqelohq.com /
// mithqal-v2-admin.varqelohq.com, both on 443) — the "app ↗" link 404'd and
// the wrong-port banner pointed at a port nginx never exposes.
// Rules: a hostname containing "-admin" maps to its customer twin by
// dropping the suffix (and vice versa by inserting it before the first
// dot); bare-host dev setups keep the adjacent-port convention
// (admin = web port + 1).
function siblingOrigin(kind) {
  const { protocol, hostname, port } = window.location;
  const isAdminHost = hostname.includes("-admin");
  if (isAdminHost || (!port && hostname.includes("."))) {
    const customerHost = isAdminHost ? hostname.replace("-admin", "") : hostname;
    const operatorHost = isAdminHost
      ? hostname
      : customerHost.replace(/^([^.]+)/, "$1-admin");
    const host = kind === "operator" ? operatorHost : customerHost;
    return `${protocol}//${host}${port ? ":" + port : ""}`;
  }
  // Dev topology: web on an even port, operator one above it.
  const p = parseInt(port || "80", 10);
  const webPort = p % 2 === 1 ? p - 1 : p;
  const target = kind === "operator" ? webPort + 1 : webPort;
  return `${protocol}//${hostname}:${target}`;
}
const OPERATOR_URL = siblingOrigin("operator") + "/admin";
const CUSTOMER_URL = siblingOrigin("customer") + "/dashboard";

const SCREENS = [
  { key: "control", label: "Control", path: "/admin" },
  { key: "record", label: "Record", path: "/admin/record" },
  { key: "fund", label: "Fund", path: "/admin/fund" },
  { key: "lab", label: "Lab", path: "/admin/lab" },
  { key: "customers", label: "Customers", path: "/admin/customers" },
  { key: "controls", label: "Actions", path: "/admin/actions" },
  { key: "services", label: "Services", path: "/admin/services" },
  { key: "rooms", label: "Rooms", path: "/admin/rooms" },
  { key: "telegram", label: "Telegram", path: "/admin/telegram" },
  { key: "website", label: "Website", path: "/admin/website" },
  { key: "audit", label: "Audit", path: "/admin/audit" },
];

// A 500 is a body too.
//
// api() resolves whatever JSON the server sent, so `(await
// api("/api/admin/health")).health` on an error response is `undefined` --
// which every screen then rendered as data, under a header that said "updated
// 0s ago" because the loader cleared the error and stamped the clock
// regardless. Nothing is trusted, and neither the clock nor the error banner
// moves, until the payload actually carried what was asked for.
function parsed(payload, key) {
  if (!payload || payload.success === false || payload[key] == null) {
    throw new Error("unreadable");
  }
  return payload[key];
}

function AdminApp() {
  const path = window.location.pathname;
  const screen = path.startsWith("/admin/record") ? "record"
    : path.startsWith("/admin/fund") ? "fund"
    : path.startsWith("/admin/lab") ? "lab"
    : path.startsWith("/admin/customers") ? "customers"
    : path.startsWith("/admin/actions") ? "controls"
    : path.startsWith("/admin/services") ? "services"
    : path.startsWith("/admin/rooms") ? "rooms"
    : path.startsWith("/admin/telegram") ? "telegram"
    : path.startsWith("/admin/website") ? "website"
    : path.startsWith("/admin/audit") ? "audit" : "control";

  const [health, setHealth] = useState(null);
  const [record, setRecord] = useState(null);
  const [actions, setActions] = useState(null);
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  // Set once we are on our way to the login page, so the refresh timer does
  // not keep firing navigations behind it.
  const leaving = useRef(false);

  const load = useCallback(async () => {
    try {
      if (screen === "control" || screen === "controls") {
        // The Actions screen needs the halt state, which lives in health.
        setHealth(parsed(await api("/api/admin/health"), "health"));
      }
      if (screen === "record") {
        const payload = await api("/api/admin/record");
        if (!payload || payload.success !== true) throw new Error("unreadable");
        setRecord(payload);
      }
      if (screen === "audit") setActions(parsed(await api("/api/admin/audit"), "actions"));
      if (screen === "customers") setUsers(parsed(await api("/api/admin/users"), "users"));
      setError(null);
      setRefreshedAt(new Date().toISOString());
    } catch (e) {
      // Not signed in is not an error message, it is a DESTINATION. This
      // rendered "Not signed in as an operator." and nothing else -- no link,
      // no form, no way forward. Opening the operator app in a fresh browser
      // produced a dead end, which is what happens when only the signed-in
      // path is ever tested.
      if (e.message === "signed-out") {
        // Once. The screens reload on a timer, and without this guard every
        // tick fires another navigation at a browser already on its way to
        // the login page.
        if (leaving.current) return;
        leaving.current = true;
        // A 401 proves any stored token is dead. Drop it, or a stale token
        // outvotes a perfectly good cookie forever: the login page accepts
        // the cookie and sends us back, we present the corpse again, 401,
        // and around we go.
        try {
          sessionStorage.removeItem("access_token");
          localStorage.removeItem("access_token");
        } catch (err) { /* storage can be denied; the cookie path still works */ }
        setError("signed-out");
        // Carry where they were headed, so signing in returns them there
        // rather than dropping them on the control room every time. replace,
        // not assign: a bounce the operator can land back on with the Back
        // button is a trap, because Back only bounces them again.
        const back = encodeURIComponent(window.location.pathname);
        window.location.replace(`/login?next=${back}`);
        return;
      }
      // The health payload is what the Control and Actions screens draw their
      // lights from. Keeping the last good copy behind an error banner would
      // paint a green kill-switch light over a system we just failed to
      // reach; dropping it puts both screens into the honest third state.
      setHealth(null);
      setError(["not-operator", "wrong-port"].includes(e.message)
        ? e.message : "unreachable");
    }
  }, [screen]);

  useEffect(() => {
    load();
    // The control room is a live view: an operator leaves it open.
    const timer = setInterval(load, screen === "control" ? 15000 : 60000);
    return () => clearInterval(timer);
  }, [load, screen]);

  return html`
    <div className="admin">
      <header className="admin-bar">
        <span className="admin-brand">Mithqal <b>operator</b></span>
        <nav className="admin-nav">
          ${SCREENS.map((s) => html`
            <a key=${s.key} href=${s.path} className=${s.key === screen ? "is-active" : ""}>
              ${s.label}
            </a>`)}
        </nav>
        <span className="admin-refreshed">
          ${refreshedAt ? `updated ${ago(refreshedAt)} ago` : ""}
        </span>
        <a className="admin-exit" href=${CUSTOMER_URL}>app ↗</a>
        <button className="admin-exit admin-logout"
                onClick=${async () => {
                  // End the shift properly: the operator app had NO way out
                  // -- the session just aged into a surprise bounce.
                  const bearer = token();
                  try {
                    await fetch('/api/auth/logout', {
                      method: 'POST',
                      headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
                    });
                  } catch (e) { /* the redirect below still ends the shift */ }
                  try { localStorage.removeItem('access_token'); } catch (e) {}
                  window.location.href = '/login';
                }}>sign out</button>
      </header>

      <main className="admin-body">
        ${error && html`
          <div className="admin-error" role="alert">
            <p>${error === "signed-out"
              ? "You are not signed in. Taking you to the sign-in page…"
              : error === "not-operator"
                ? "You are signed in, but this account is not an operator."
                : error === "wrong-port"
                  ? `This is the customer app's address. The operator app has its own — open it at ${OPERATOR_URL} and sign in there.`
                  : "Could not reach the server. Nothing here is current."}</p>
            ${error === "wrong-port"
              ? html`<a className="admin-signin" href=${OPERATOR_URL}>Open the operator app</a>`
              : error === "signed-out"
              ? html`<a className="admin-signin"
                        href=${`/login?next=${encodeURIComponent(window.location.pathname)}`}>
                       Sign in
                     </a>`
              : error === "not-operator"
                ? html`<button className="admin-signin" onClick=${signOutThenSignIn}>
                         Sign in as an operator
                       </button>`
                : html`<button className="admin-signin" onClick=${load}>Try again</button>`}
          </div>`}
        ${screen === "control" && html`<${ControlRoom} health=${health} />`}
        ${screen === "record" && html`<${RecordScreen} record=${record} /><${FilterPower} />`}
        ${screen === "fund" && html`<${FundEventsCard} /><${ObserverCard} />`}
        ${screen === "lab" && html`<${LabScreen} />`}
        ${screen === "customers" && html`<${CustomersScreen} users=${users} onReload=${load} />`}
        ${screen === "controls" && html`<${ControlsScreen} health=${health} onReload=${load} />`}
        ${screen === "services" && html`<${ServicesScreen} />`}
        ${screen === "rooms" && html`<${RoomsScreen} />`}
        ${screen === "telegram" && html`<${TelegramScreen} />`}
        ${screen === "website" && html`<${WebsiteScreen} />`}
        ${screen === "audit" && html`<${AuditScreen} actions=${actions} />`}
      </main>
    </div>`;
}

createRoot(document.getElementById("admin-root")).render(html`<${AdminApp} />`);
